/**
 * V70 Tournament Apply — Phase A: 관리자 참가신청 빌더
 *
 * 기능
 *   - 신청서 목록 (status 필터 + 신규 생성)
 *   - 신청서 상세 (제목/설명/기간/mode/teamSize/approvalMode/상태)
 *   - 필드 에디터 (team/member scope + type + required + options + order)
 *   - preset 버튼 (팀명 / 닉네임 1 click 추가)
 *   - 미리보기 (참가자 시점 폼 렌더)
 *   - 상태 전이 (publish/pause/resume/close/archive)
 *   - 변경 이력
 *
 * 설계 변경 사유 (Phase A 설계안 §5.2):
 *   components/applyAdmin/ 폴더 분리 대신 single-file 통합 — Phase A 는 self-contained,
 *   다른 모듈이 import 안 함. 유지보수 상 single-file 이 import 복잡성 감소에 유리.
 *   향후 Phase B/C 에서 재사용 요구가 생기면 분리 리팩토링 예정.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
// V70 Phase B 긴급 수정 (2026-10-20) — 참가자 화면과 상태 표시 **단일 소스** 공유
import {
  computeEffectiveFormStatus,
  effectiveStatusLabel,
  effectiveStatusColor,
} from '../tournament/shared/applyFormStatus';
// V70 Phase B UX 개선 (2026-10-20) — Admin 신청자 섹션 UID → 이름 변환용
import { useEmployees } from '../../shared/hooks/useEmployees';
// V70 Phase C — 대진표 공용 모듈
import { BracketTreeView } from '../tournament/shared/BracketTreeView';
import { matchSideLabel } from './tournamentMatchSideName';
import { tournamentAuthorLabel } from '../tournament/shared/authorLabel';
import RichTextEditor from '../../shared/components/RichTextEditor';
import { sanitizeAnnouncementHtml } from '../../shared/components/RichContentViewer';
import { useAnnouncementUpload } from '../../shared/hooks/useAnnouncements';
import type {
  BracketStatus as BrStatus, PublicBracketView, PublicMatchView, PublicParticipantView,
  Side as BrSide, BestOfMode,
} from '../tournament/shared/bracketTypes';
import { statusLabel as brStatusLabel, statusColor as brStatusColor, roundLabelOf } from '../tournament/shared/bracketTypes';

// ─── 타입 ───
type FormMode = 'solo' | 'team';
type FormStatus = 'draft' | 'open' | 'paused' | 'closed' | 'archived';
type ApprovalMode = 'auto' | 'manual';
type FieldType = 'short' | 'long' | 'select' | 'checkbox';
type FieldScope = 'team' | 'member';
type StatusAction = 'publish' | 'pause' | 'resume' | 'close' | 'archive';

interface Field {
  fieldId?: string;
  label: string;
  type: FieldType;
  required: boolean;
  scope: FieldScope;
  options: string[] | null;
  order: number;
  deleted?: boolean;
}

interface ApplyForm {
  formId: string;
  title: string;
  description: string;
  applyStartAt: { toMillis?: () => number; _seconds?: number };
  applyEndAt: { toMillis?: () => number; _seconds?: number };
  publicStatus: FormStatus;
  mode: FormMode;
  teamSize: number | null;
  approvalMode: ApprovalMode;
  fields: Field[];
  // V70 Phase B UX 개선 (2026-10-20) — 대표 이미지 (선택적)
  coverImageUrl: string | null;
  coverImagePath: string | null;
  firstApplicationAt: { toMillis?: () => number; _seconds?: number } | null;
  schemaVersion: number;
  version: number;
  createdByName: string;
  updatedByName: string;
}

interface FormSummary {
  formId: string;
  title: string;
  description: string;
  mode: FormMode;
  teamSize: number | null;
  approvalMode: ApprovalMode;
  publicStatus: FormStatus;
  applyStartAt: any;
  applyEndAt: any;
  schemaVersion: number;
  firstApplicationAt: any;
  createdByName: string;
  updatedByName: string;
  fieldCount: number;
  version: number;
}

interface HistoryEntry {
  historyId: string;
  formId: string;
  action: 'create' | 'update' | 'statusChange';
  changedByName: string;
  changedAt: any;
  snapshot: ApplyForm;
}

// ─── Firestore timestamp 변환 ───
function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts._seconds === 'number') return ts._seconds * 1000;
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function msToDatetimeLocal(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hr = String(d.getHours()).padStart(2, '0');
  const mn = String(d.getMinutes()).padStart(2, '0');
  return `${yr}-${mo}-${da}T${hr}:${mn}`;
}

// V70 Phase B 긴급 수정 (2026-10-20) — 상태 표시 공용 유틸 재사용 (참가자 화면과 라벨 일치).
// 라벨 필터 (목록 상단 "전체/open/..." 드롭다운) — publicStatus 선택지는 BE 저장값 기준 유지.
function statusLabel(s: FormStatus): string {
  switch (s) {
    case 'draft':    return '작성중';
    case 'open':     return '공개 (open)';
    case 'paused':   return '일시중지';
    case 'closed':   return '종료';
    case 'archived': return '보관';
  }
}

// ─── 라벨 상수 ───
const MODE_LABEL = { solo: '개인전', team: '팀전' };
const APPROVAL_LABEL = { auto: '자동 승인', manual: '수동 승인' };
const TYPE_LABEL: Record<FieldType, string> = { short: '단답형', long: '장문형', select: '선택형', checkbox: '체크박스' };

// ─── 메인 페이지 ───
export function TournamentApplyAdminPage() {
  const [forms, setForms] = useState<FormSummary[]>([]);
  const [filter, setFilter] = useState<FormStatus | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const fns = getFunctions();
      const listFn = httpsCallable<{ statusFilter?: FormStatus | 'all'; limit?: number }, { forms: FormSummary[] }>(fns, 'listTournamentApplyForms');
      const r = await listFn({ statusFilter: filter, limit: 200 });
      setForms(r.data.forms || []);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => forms, [forms]);

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>V70 참가신청 관리</h1>
        <button
          onClick={() => setShowCreate(true)}
          style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
        >
          + 신규 신청서
        </button>
      </div>

      {err && (
        <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', marginBottom: 16, fontSize: 13 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>
        {/* 좌측: 목록 */}
        <div>
          <section style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>상태 필터</div>
            {(['all', 'draft', 'open', 'paused', 'closed', 'archived'] as const).map((s) => (
              <label key={s} style={{ display: 'block', fontSize: 13, padding: '4px 0', cursor: 'pointer' }}>
                <input type="radio" checked={filter === s} onChange={() => setFilter(s)} style={{ marginRight: 6 }} />
                {s === 'all' ? '전체' : statusLabel(s as FormStatus)}
              </label>
            ))}
          </section>

          <section style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              신청서 목록 ({filtered.length})
            </div>
            {loading && <div style={{ fontSize: 13, color: '#6b7280' }}>로딩 중...</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: 20 }}>
                신청서 없음
              </div>
            )}
            {filtered.map((f) => (
              <button
                key={f.formId}
                onClick={() => setSelectedId(f.formId)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: 8, marginBottom: 4,
                  background: selectedId === f.formId ? '#eff6ff' : 'white',
                  border: '1px solid ' + (selectedId === f.formId ? '#2563eb' : '#e5e7eb'),
                  borderRadius: 6, cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{f.title}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {(() => {
                    const eff = computeEffectiveFormStatus(f.publicStatus, f.applyStartAt, f.applyEndAt);
                    return (
                      <span style={{ color: effectiveStatusColor(eff), fontWeight: 600 }}>
                        ● {effectiveStatusLabel(eff)}
                      </span>
                    );
                  })()}
                  {' · '}{MODE_LABEL[f.mode]}{f.mode === 'team' && ` ${f.teamSize}명`}
                  {' · '}필드 {f.fieldCount}
                </div>
              </button>
            ))}
          </section>
        </div>

        {/* 우측: 상세 */}
        <div>
          {!selectedId && !showCreate && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14, border: '1px dashed #e5e7eb', borderRadius: 8 }}>
              신청서를 선택하거나 신규 생성하세요.
            </div>
          )}
          {showCreate && (
            <FormEditor
              mode="create"
              formId={null}
              onDone={async (newId) => {
                setShowCreate(false);
                await reload();
                if (newId) setSelectedId(newId);
              }}
              onCancel={() => setShowCreate(false)}
            />
          )}
          {!showCreate && selectedId && (
            <FormEditor
              mode="edit"
              formId={selectedId}
              onDone={async () => { await reload(); }}
              onCancel={() => setSelectedId(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default TournamentApplyAdminPage;

// ─── FormEditor ───
interface FormEditorProps {
  mode: 'create' | 'edit';
  formId: string | null;
  onDone: (newId?: string) => void | Promise<void>;
  onCancel: () => void;
}

function FormEditor({ mode, formId, onDone, onCancel }: FormEditorProps) {
  const [form, setForm] = useState<ApplyForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  // V70 Phase C — 3번째 탭 '대진표' 추가
  // V70 QA 신규 기능 #2 수정 (2026-10-22) — 공지사항을 별도 탭으로 분리하여 접근성 개선.
  //   기존: BracketTab 하단 섹션 → 대진표 탭 활성 시만 노출 → 사용자가 찾기 어려움.
  //   변경: 신규 'board' 탭 → 대진표 생성 여부와 무관하게 이용 가능.
  // V70G5A Bug 6 — 공지사항은 대시보드 전역 AnnouncementsSection 으로 이관됨. 'board' 탭 제거.
  const [activeTab, setActiveTab] = useState<'settings' | 'applications' | 'bracket'>('settings');

  // Edit mode: 서버 로드
  const loadForm = useCallback(async () => {
    if (mode !== 'edit' || !formId) return;
    setLoading(true);
    setErr(null);
    try {
      const fns = getFunctions();
      const getFn = httpsCallable<{ formId: string }, { form: ApplyForm; isLocked: boolean }>(fns, 'getTournamentApplyForm');
      const histFn = httpsCallable<{ formId: string }, { history: HistoryEntry[] }>(fns, 'getTournamentApplyFormHistory');
      const [r1, r2] = await Promise.all([getFn({ formId }), histFn({ formId })]);
      setForm(r1.data.form);
      setHistory(r2.data.history || []);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [mode, formId]);

  useEffect(() => { loadForm(); }, [loadForm]);

  // Create mode: 기본값 state
  const [draft, setDraft] = useState<ApplyForm>(() => ({
    formId: '',
    title: '',
    description: '',
    applyStartAt: { toMillis: () => Date.now() + 24 * 3600 * 1000 },
    applyEndAt: { toMillis: () => Date.now() + 8 * 24 * 3600 * 1000 },
    publicStatus: 'draft',
    mode: 'solo',
    teamSize: null,
    approvalMode: 'manual',
    fields: [],
    coverImageUrl: null,
    coverImagePath: null,
    firstApplicationAt: null,
    schemaVersion: 1,
    version: 1,
    createdByName: '',
    updatedByName: '',
  }));

  const working = mode === 'create' ? draft : form;
  const setWorking = mode === 'create' ? setDraft : (updater: (f: ApplyForm) => ApplyForm) => {
    setForm((prev) => (prev ? updater(prev) : prev));
  };

  const isLocked = !!working?.firstApplicationAt;

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>로딩 중...</div>;
  if (!working) return <div style={{ padding: 20, color: '#991b1b' }}>{err || '불러오기 실패'}</div>;

  async function save() {
    if (!working) return;
    setSaving(true);
    setErr(null);
    try {
      const fns = getFunctions();
      if (mode === 'create') {
        const createFn = httpsCallable<any, { success: true; formId: string; reused: boolean }>(fns, 'createTournamentApplyForm');
        const payload: any = {
          title: working.title,
          description: working.description,
          mode: working.mode,
          teamSize: working.mode === 'team' ? working.teamSize : null,
          applyStartAt: tsToMs(working.applyStartAt),
          applyEndAt: tsToMs(working.applyEndAt),
          approvalMode: working.approvalMode,
          fields: working.fields.map((f) => ({
            label: f.label, type: f.type, required: f.required, scope: f.scope,
            options: f.options || undefined, order: f.order,
          })),
          clientRequestId: `create-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        };
        const r = await createFn(payload);
        onDone(r.data.formId);
      } else {
        if (!formId) return;
        const updateFn = httpsCallable<any, { success: true }>(fns, 'updateTournamentApplyForm');
        const payload: any = {
          formId,
          updates: {
            title: working.title,
            description: working.description,
            applyStartAt: tsToMs(working.applyStartAt),
            applyEndAt: tsToMs(working.applyEndAt),
            mode: working.mode,
            teamSize: working.mode === 'team' ? working.teamSize : null,
            approvalMode: working.approvalMode,
            fields: working.fields.map((f) => ({
              fieldId: f.fieldId, label: f.label, type: f.type, required: f.required, scope: f.scope,
              options: f.options || undefined, order: f.order, deleted: f.deleted,
            })),
          },
          expectedVersion: working.version,
          clientRequestId: `update-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        };
        await updateFn(payload);
        await loadForm();
        onDone();
      }
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  async function doStatus(action: StatusAction) {
    if (!formId || !working) return;
    // V70 Phase B 긴급 수정 (2026-10-20) — 사용자 지시 §8: 신청기간이 지난 양식을 공개 시 경고.
    if (action === 'publish') {
      const endMs = tsToMs(working.applyEndAt);
      const startMs = tsToMs(working.applyStartAt);
      const nowMs = Date.now();
      if (endMs > 0 && nowMs > endMs) {
        if (!window.confirm(
          '⚠ 접수기간이 이미 종료된 양식입니다.\n\n' +
          `종료 시각: ${new Date(endMs).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}\n` +
          `현재 시각: ${new Date(nowMs).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}\n\n` +
          '공개해도 참가자는 신청할 수 없습니다. (상태 뱃지: "접수 종료")\n' +
          '그래도 공개하시겠습니까? (취소 후 applyEndAt 를 연장할 수 있습니다.)'
        )) return;
      } else if (startMs > 0 && nowMs < startMs) {
        if (!window.confirm(
          `접수 예정 양식입니다 (시작: ${new Date(startMs).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}).\n\n` +
          '공개하시겠습니까? 시작 시각 이후 접수가 시작됩니다.'
        )) return;
      } else {
        if (!window.confirm(`'${action}' 수행하시겠습니까?`)) return;
      }
    } else {
      if (!window.confirm(`'${action}' 수행하시겠습니까?`)) return;
    }
    setSaving(true);
    setErr(null);
    try {
      const fns = getFunctions();
      const statusFn = httpsCallable<any, any>(fns, 'updateTournamentApplyFormStatus');
      await statusFn({ formId, action, clientRequestId: `s-${action}-${Date.now()}` });
      await loadForm();
      onDone();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  // field 조작
  const addField = (scope: FieldScope) => {
    const w = working!;
    const maxOrder = Math.max(0, ...w.fields.map((f) => f.order));
    setWorking((prev: ApplyForm) => ({
      ...prev,
      fields: [...prev.fields, { label: '', type: 'short', required: false, scope, options: null, order: maxOrder + 1, deleted: false } as Field],
    }));
  };
  const addPresetTeamName = () => {
    const w = working!;
    const maxOrder = Math.max(0, ...w.fields.map((f) => f.order));
    setWorking((prev: ApplyForm) => ({
      ...prev,
      fields: [...prev.fields, { label: '팀명', type: 'short', required: true, scope: 'team', options: null, order: maxOrder + 1, deleted: false } as Field],
    }));
  };
  const addPresetNickname = () => {
    const w = working!;
    const maxOrder = Math.max(0, ...w.fields.map((f) => f.order));
    setWorking((prev: ApplyForm) => ({
      ...prev,
      fields: [...prev.fields, { label: '닉네임', type: 'short', required: true, scope: 'member', options: null, order: maxOrder + 1, deleted: false } as Field],
    }));
  };
  const updateField = (idx: number, patch: Partial<Field>) => {
    setWorking((prev: ApplyForm) => ({
      ...prev,
      fields: prev.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));
  };
  const removeField = (idx: number) => {
    if (isLocked) {
      setErr('잠금 상태에서 field 삭제 불가');
      return;
    }
    setWorking((prev: ApplyForm) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }));
  };
  const moveField = (idx: number, dir: -1 | 1) => {
    setWorking((prev: ApplyForm) => {
      const list = [...prev.fields];
      const target = idx + dir;
      if (target < 0 || target >= list.length) return prev;
      const a = list[idx]; const b = list[target];
      list[idx] = { ...b, order: a.order };
      list[target] = { ...a, order: b.order };
      return { ...prev, fields: list };
    });
  };

  const canPublish = working.publicStatus === 'draft';
  const canPause = working.publicStatus === 'open';
  const canResume = working.publicStatus === 'paused';
  const canClose = working.publicStatus === 'open' || working.publicStatus === 'paused';
  const canArchive = working.publicStatus === 'closed';

  return (
    <div style={{ padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>
            {mode === 'create' ? '신규 신청서' : working.title || '(제목 없음)'}
          </h2>
          {mode === 'edit' && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              formId: {working.formId} · schema v{working.schemaVersion} · version {working.version}
              {isLocked && <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 600 }}>🔒 접수 시작됨 (핵심 설정 잠금)</span>}
            </div>
          )}
        </div>
        <div>
          {(() => {
            const eff = computeEffectiveFormStatus(working.publicStatus, working.applyStartAt, working.applyEndAt);
            return (
              <span style={{ padding: '4px 10px', borderRadius: 4, background: effectiveStatusColor(eff), color: 'white', fontSize: 12, fontWeight: 600 }}>
                {effectiveStatusLabel(eff)}
              </span>
            );
          })()}
        </div>
      </div>

      {err && (
        <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', marginBottom: 12, fontSize: 13 }}>
          {err}
        </div>
      )}

      {/* V70 Phase B UX 개선 — 탭 바 (edit 모드에서만, create 모드는 설정만) */}
      {mode === 'edit' && working.formId && (
        <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 16 }}>
          <button onClick={() => setActiveTab('settings')} style={tabBtnStyle(activeTab === 'settings')}>양식 설정</button>
          <button onClick={() => setActiveTab('applications')} style={tabBtnStyle(activeTab === 'applications')}>신청현황</button>
          <button onClick={() => setActiveTab('bracket')} style={tabBtnStyle(activeTab === 'bracket')}>대진표</button>
          {/* V70 QA 통합 공지 (2026-10-23) — 이벤트별 공지사항 탭 제거. 공지는 게임센터 대시보드 상단 전역 섹션 사용. */}
        </div>
      )}

      {/* '신청현황' 탭 */}
      {mode === 'edit' && activeTab === 'applications' && working.formId && (
        <ApplicationsSection formId={working.formId} approvalMode={working.approvalMode} form={working} />
      )}

      {/* V70 Phase C — '대진표' 탭 */}
      {mode === 'edit' && activeTab === 'bracket' && working.formId && (
        <BracketTab formId={working.formId} formTitle={working.title} applyEndMs={tsToMs(working.applyEndAt)} publicStatus={working.publicStatus} />
      )}

      {/* V70 QA 통합 공지 (2026-10-23) — BoardAdminSection 호출 제거 (이벤트별 공지 UI 폐기).
            전역 공지는 TournamentDashboardPage.tsx AnnouncementsSection 사용. */}

      {/* '양식 설정' 탭 (create 모드 또는 settings 활성) — 아래 섹션 전부 포함 */}
      {(mode === 'create' || activeTab === 'settings') && (
      <>
      {/* 기본 정보 */}
      <section style={{ marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>기본 정보</div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>대회명</label>
          <input
            type="text"
            value={working.title}
            onChange={(e) => setWorking((p: ApplyForm) => ({ ...p, title: e.target.value }))}
            maxLength={100}
            style={{ width: '100%', padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
          />
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>설명</label>
          <textarea
            value={working.description}
            onChange={(e) => setWorking((p: ApplyForm) => ({ ...p, description: e.target.value }))}
            maxLength={2000}
            rows={3}
            style={{ width: '100%', padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>접수 시작</label>
            <input
              type="datetime-local"
              value={msToDatetimeLocal(tsToMs(working.applyStartAt))}
              onChange={(e) => setWorking((p: ApplyForm) => ({ ...p, applyStartAt: { toMillis: () => new Date(e.target.value).getTime() } }))}
              style={{ width: '100%', padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#4b5563', marginBottom: 4 }}>접수 마감</label>
            <input
              type="datetime-local"
              value={msToDatetimeLocal(tsToMs(working.applyEndAt))}
              onChange={(e) => setWorking((p: ApplyForm) => ({ ...p, applyEndAt: { toMillis: () => new Date(e.target.value).getTime() } }))}
              style={{ width: '100%', padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, color: '#4b5563', marginRight: 6 }}>모드{isLocked && ' 🔒'}:</label>
            {(['solo', 'team'] as const).map((m) => (
              <label key={m} style={{ marginRight: 10, fontSize: 13, cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.6 : 1 }}>
                <input
                  type="radio"
                  checked={working.mode === m}
                  disabled={isLocked}
                  onChange={() => setWorking((p: ApplyForm) => ({ ...p, mode: m, teamSize: m === 'team' ? (p.teamSize || 3) : null }))}
                /> {MODE_LABEL[m]}
              </label>
            ))}
          </div>
          {working.mode === 'team' && (
            <div>
              <label style={{ fontSize: 12, color: '#4b5563', marginRight: 6 }}>팀 인원{isLocked && ' 🔒'}:</label>
              <input
                type="number"
                min={2}
                max={20}
                value={working.teamSize ?? 3}
                disabled={isLocked}
                onChange={(e) => setWorking((p: ApplyForm) => ({ ...p, teamSize: parseInt(e.target.value) || 2 }))}
                style={{ width: 60, padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
              />
              <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 4 }}>(팀장 포함)</span>
            </div>
          )}
          <div>
            <label style={{ fontSize: 12, color: '#4b5563', marginRight: 6 }}>승인{isLocked && ' 🔒'}:</label>
            {(['auto', 'manual'] as const).map((a) => (
              <label key={a} style={{ marginRight: 10, fontSize: 13, cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.6 : 1 }}>
                <input
                  type="radio"
                  checked={working.approvalMode === a}
                  disabled={isLocked}
                  onChange={() => setWorking((p: ApplyForm) => ({ ...p, approvalMode: a }))}
                /> {APPROVAL_LABEL[a]}
              </label>
            ))}
          </div>
        </div>
      </section>

      {/* V70 Phase B UX 개선 — 대표 이미지 (edit 모드에서만, 양식 저장 후 사용 가능) */}
      {mode === 'edit' && formId && (
        <CoverImageSection
          formId={formId}
          coverImageUrl={working.coverImageUrl ?? null}
          coverImagePath={working.coverImagePath ?? null}
          expectedVersion={working.version}
          onUpdated={(next) => {
            setWorking((p: ApplyForm) => ({
              ...p,
              coverImageUrl: next.coverImageUrl,
              coverImagePath: next.coverImagePath,
              version: next.version,
            }));
            loadForm();
          }}
        />
      )}
      {mode === 'create' && (
        <section style={{ marginBottom: 16, padding: 12, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
          대표 이미지는 양식을 먼저 저장한 뒤 "양식 설정" 탭에서 업로드할 수 있습니다.
        </section>
      )}

      {/* 필드 에디터 */}
      <section style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>참가 신청 항목 ({working.fields.filter((f) => !f.deleted).length})</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!isLocked && (
              <>
                {working.mode === 'team' && (
                  <button onClick={() => addField('team')} style={{ padding: '4px 10px', background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                    + 팀 공통 필드
                  </button>
                )}
                <button onClick={() => addField('member')} style={{ padding: '4px 10px', background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                  + {working.mode === 'team' ? '팀원' : '참가자'} 필드
                </button>
                {working.mode === 'team' && (
                  <button onClick={addPresetTeamName} style={{ padding: '4px 10px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                    preset: 팀명
                  </button>
                )}
                <button onClick={addPresetNickname} style={{ padding: '4px 10px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                  preset: 닉네임
                </button>
              </>
            )}
          </div>
        </div>
        {working.fields.filter((f) => !f.deleted).length === 0 && (
          <div style={{ padding: 16, color: '#6b7280', fontSize: 13, textAlign: 'center', border: '1px dashed #d1d5db', borderRadius: 6 }}>
            항목 없음. 위 버튼으로 추가하세요.
          </div>
        )}
        {working.mode === 'team' && (
          <FieldGroup
            label="팀 공통"
            fields={working.fields}
            scope="team"
            isLocked={isLocked}
            onUpdate={updateField}
            onRemove={removeField}
            onMove={moveField}
          />
        )}
        <FieldGroup
          label={working.mode === 'team' ? '팀원별' : '참가자별'}
          fields={working.fields}
          scope="member"
          isLocked={isLocked}
          onUpdate={updateField}
          onRemove={removeField}
          onMove={moveField}
        />
      </section>

      {/* 액션 */}
      <section style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={save}
          disabled={saving}
          style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}
        >
          {saving ? '저장중...' : (mode === 'create' ? '생성' : '저장')}
        </button>
        <button onClick={() => setShowPreview(!showPreview)} style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}>
          {showPreview ? '미리보기 닫기' : '미리보기'}
        </button>
        {mode === 'edit' && (
          <>
            <div style={{ borderLeft: '1px solid #e5e7eb', margin: '0 8px' }} />
            {canPublish && <button onClick={() => doStatus('publish')} style={actionBtnStyle('#059669')}>공개 (publish)</button>}
            {canPause && <button onClick={() => doStatus('pause')} style={actionBtnStyle('#d97706')}>일시중지 (pause)</button>}
            {canResume && <button onClick={() => doStatus('resume')} style={actionBtnStyle('#059669')}>재개 (resume)</button>}
            {canClose && <button onClick={() => doStatus('close')} style={actionBtnStyle('#dc2626')}>마감 (close)</button>}
            {canArchive && <button onClick={() => doStatus('archive')} style={actionBtnStyle('#4b5563')}>보관 (archive)</button>}
          </>
        )}
        <button onClick={onCancel} style={{ marginLeft: 'auto', padding: '8px 16px', background: 'white', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}>
          닫기
        </button>
      </section>

      {/* 미리보기 */}
      {showPreview && <FormPreview form={working} />}

      {/* 변경 이력 */}
      {mode === 'edit' && history.length > 0 && (
        <section style={{ padding: 12, background: '#f9fafb', borderRadius: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>변경 이력 ({history.length})</div>
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {history.map((h) => (
              <div key={h.historyId} style={{ fontSize: 12, padding: '6px 0', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontWeight: 600 }}>{h.action}</span>
                {' · '}{h.changedByName}
                {' · '}{new Date(tsToMs(h.changedAt)).toLocaleString('ko-KR')}
              </div>
            ))}
          </div>
        </section>
      )}
      </>
      )}
    </div>
  );
}

function actionBtnStyle(color: string): React.CSSProperties {
  return { padding: '8px 12px', background: color, color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' };
}

// V70 Phase B UX 개선 — 탭 버튼 스타일
function tabBtnStyle(active: boolean): React.CSSProperties {
  return {
    padding: '10px 20px',
    background: 'transparent',
    color: active ? '#2563eb' : '#6b7280',
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    marginBottom: '-2px',
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
  };
}

// ─── FieldGroup ───
interface FieldGroupProps {
  label: string;
  fields: Field[];
  scope: FieldScope;
  isLocked: boolean;
  onUpdate: (idx: number, patch: Partial<Field>) => void;
  onRemove: (idx: number) => void;
  onMove: (idx: number, dir: -1 | 1) => void;
}

function FieldGroup({ label, fields, scope, isLocked, onUpdate, onRemove, onMove }: FieldGroupProps) {
  const items = fields
    .map((f, idx) => ({ f, idx }))
    .filter(({ f }) => f.scope === scope && !f.deleted)
    .sort((a, b) => a.f.order - b.f.order);
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 6 }}>{label}</div>
      {items.map(({ f, idx }, i) => (
        <FieldItemCard
          key={idx}
          field={f}
          isLocked={isLocked}
          onUpdate={(patch) => onUpdate(idx, patch)}
          onRemove={() => onRemove(idx)}
          onMoveUp={i > 0 ? () => onMove(idx, -1) : null}
          onMoveDown={i < items.length - 1 ? () => onMove(idx, 1) : null}
        />
      ))}
    </div>
  );
}

interface FieldItemCardProps {
  field: Field;
  isLocked: boolean;
  onUpdate: (patch: Partial<Field>) => void;
  onRemove: () => void;
  onMoveUp: (() => void) | null;
  onMoveDown: (() => void) | null;
}

function FieldItemCard({ field, isLocked, onUpdate, onRemove, onMoveUp, onMoveDown }: FieldItemCardProps) {
  const [optionsText, setOptionsText] = useState<string>((field.options || []).join('\n'));
  // field 가 바뀔 때 textarea 재동기화. field.fieldId 로 식별하되 options 가 외부에서 바뀌어도 반영.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOptionsText((field.options || []).join('\n')); }, [field.fieldId]);
  const typeDisabled = isLocked;
  const requiredDisabled = isLocked;
  const optionsDisabled = isLocked;
  return (
    <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 6, background: 'white' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <input
          type="text"
          value={field.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          maxLength={100}
          placeholder="항목명"
          style={{ flex: 1, padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
        />
        <select
          value={field.type}
          disabled={typeDisabled}
          onChange={(e) => {
            const newType = e.target.value as FieldType;
            onUpdate({
              type: newType,
              options: (newType === 'select' || newType === 'checkbox') ? (field.options || []) : null,
            });
          }}
          style={{ padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, opacity: typeDisabled ? 0.6 : 1 }}
        >
          {(Object.keys(TYPE_LABEL) as FieldType[]).map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
        <label style={{ fontSize: 12, opacity: requiredDisabled ? 0.6 : 1 }}>
          <input type="checkbox" checked={field.required} disabled={requiredDisabled} onChange={(e) => onUpdate({ required: e.target.checked })} /> 필수
        </label>
        {onMoveUp && <button onClick={onMoveUp} style={{ padding: '2px 6px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>▲</button>}
        {onMoveDown && <button onClick={onMoveDown} style={{ padding: '2px 6px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>▼</button>}
        {!isLocked && (
          <button onClick={onRemove} style={{ padding: '2px 6px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>삭제</button>
        )}
      </div>
      {(field.type === 'select' || field.type === 'checkbox') && (
        <div style={{ fontSize: 12 }}>
          <div style={{ marginBottom: 2, color: '#4b5563' }}>선택지 (한 줄 1개{optionsDisabled && ', 🔒'})</div>
          <textarea
            value={optionsText}
            disabled={optionsDisabled}
            rows={3}
            onChange={(e) => {
              setOptionsText(e.target.value);
              const opts = e.target.value.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
              onUpdate({ options: opts });
            }}
            style={{ width: '100%', padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, opacity: optionsDisabled ? 0.6 : 1 }}
          />
        </div>
      )}
    </div>
  );
}

// ─── FormPreview ───
function FormPreview({ form }: { form: ApplyForm }) {
  const active = form.fields.filter((f) => !f.deleted).sort((a, b) => a.order - b.order);
  const teamFields = active.filter((f) => f.scope === 'team');
  const memberFields = active.filter((f) => f.scope === 'member');
  return (
    <section style={{ marginBottom: 16, padding: 16, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#92400e' }}>참가자 시점 미리보기</div>
      <div style={{ padding: 12, background: 'white', borderRadius: 4 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{form.title || '(제목 없음)'}</h3>
        {form.description && <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{form.description}</p>}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          {MODE_LABEL[form.mode]}{form.mode === 'team' && ` · 팀 ${form.teamSize}명 (팀장 포함)`}
        </div>
        {form.mode === 'team' && teamFields.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#4b5563' }}>팀 공통</div>
            {teamFields.map((f) => <PreviewInput key={f.fieldId || f.label + f.order} field={f} />)}
          </>
        )}
        <div style={{ fontSize: 13, fontWeight: 600, margin: '12px 0 6px', color: '#4b5563' }}>
          {form.mode === 'team' ? '팀원별' : '참가자'}
        </div>
        {form.mode === 'team' && (
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>※ {form.teamSize}명 각각 입력</div>
        )}
        {memberFields.map((f) => <PreviewInput key={f.fieldId || f.label + f.order} field={f} />)}
      </div>
    </section>
  );
}

function PreviewInput({ field }: { field: Field }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 3 }}>
        {field.label || '(미입력)'} {field.required && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      {field.type === 'short' && <input type="text" disabled style={{ width: '100%', padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: '#f9fafb' }} />}
      {field.type === 'long' && <textarea disabled rows={2} style={{ width: '100%', padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: '#f9fafb' }} />}
      {field.type === 'select' && (
        <select disabled style={{ width: '100%', padding: 4, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: '#f9fafb' }}>
          <option>선택...</option>
          {(field.options || []).map((o) => <option key={o}>{o}</option>)}
        </select>
      )}
      {field.type === 'checkbox' && (
        <div>
          {(field.options || []).map((o) => (
            <label key={o} style={{ display: 'block', fontSize: 12 }}>
              <input type="checkbox" disabled /> {o}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// V70 Phase B — 신청자 관리 섹션 (양식별 신청자 목록 + 승인/반려 + 집계)
// ─────────────────────────────────────────────────────────────────────────────

interface AppRow {
  applicationId: string;
  formId: string;
  ownerUid: string;
  ownerDisplayName: string;
  ownerOrgUnitId: string | null;
  mode: 'solo' | 'team';
  teamSize: number | null;
  memberUids: string[];
  teamAnswers: Record<string, string | string[]>;
  memberAnswers: Record<string, Record<string, string | string[]>>;
  status: 'submitted' | 'approved' | 'rejected' | 'cancelled';
  submittedAt: { toMillis?: () => number; _seconds?: number };
  updatedAt?: { toMillis?: () => number; _seconds?: number };
  approvedAt?: { toMillis?: () => number; _seconds?: number } | null;
  approvedByName?: string | null;
  rejectedAt?: { toMillis?: () => number; _seconds?: number } | null;
  rejectedByName?: string | null;
  cancelledAt?: { toMillis?: () => number; _seconds?: number } | null;
  rejectReason: string | null;
  version: number;
}

interface AppAggregate {
  total: number;
  submitted: number;
  approved: number;
  rejected: number;
  cancelled: number;
  uniqueParticipants: number;
  teamCount: number;
  soloCount: number;
}

function ApplicationsSection({ formId, approvalMode, form }: { formId: string; approvalMode: ApprovalMode; form?: ApplyForm }) {
  const [applications, setApplications] = useState<AppRow[]>([]);
  const [aggregate, setAggregate] = useState<AppAggregate | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'approved' | 'rejected' | 'cancelled'>('all');
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  // V70 Phase B UX 개선 — 신청 상세 모달 + UID 이름 변환
  const [selectedApp, setSelectedApp] = useState<AppRow | null>(null);
  const { employees: directoryEmployees } = useEmployees({ forTransfer: true });
  const uidToEmp = useMemo(() => {
    const map = new Map<string, { name: string; employeeNumber: string; orgUnitName: string }>();
    for (const e of directoryEmployees || []) {
      map.set(e.id, { name: e.nameKo || '-', employeeNumber: e.employeeNumber || '', orgUnitName: e.orgUnitName || '' });
    }
    return map;
  }, [directoryEmployees]);
  function displayUid(uid: string, fallback?: string | null): string {
    const emp = uidToEmp.get(uid);
    if (emp) return emp.employeeNumber ? `${emp.name} (${emp.employeeNumber})` : emp.name;
    return fallback || '(이름 조회 중…)';
  }
  function teamNameOf(a: AppRow): string | null {
    // 양식에 scope='team' + label 포함 "팀명" 필드가 있으면 그 답변을 반환
    if (!form) return null;
    const teamField = form.fields.find((f: Field) => !f.deleted && f.scope === 'team' && /팀\s*명|team\s*name/i.test(f.label));
    if (!teamField || !teamField.fieldId) return null;
    const v = a.teamAnswers?.[teamField.fieldId];
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const fns = getFunctions();
        const r = await httpsCallable<
          { formId: string; statusFilter: typeof statusFilter; limit?: number },
          { applications: AppRow[]; aggregate: AppAggregate }
        >(fns, 'listTournamentApplicationsByForm')({ formId, statusFilter, limit: 200 });
        if (cancelled) return;
        setApplications(r.data.applications || []);
        setAggregate(r.data.aggregate);
      } catch (err) {
        if (cancelled) return;
        setError((err as { message?: string })?.message || '조회 실패');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formId, statusFilter, refreshKey]);

  async function handleApprove(ownerUid: string): Promise<void> {
    setActionBusy(ownerUid); setError(null);
    try {
      await httpsCallable(getFunctions(), 'approveTournamentApplication')({
        formId, ownerUid, clientRequestId: `admin-approve-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      // V70 QA 수정 #3 (2026-10-21) — 성공 시 상세 팝업 자동 닫기 + 목록 즉시 갱신. 실패 시 팝업 유지.
      setSelectedApp(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError((err as { message?: string })?.message || '승인 실패');
    } finally {
      setActionBusy(null);
    }
  }

  async function handleReject(ownerUid: string): Promise<void> {
    const reason = window.prompt('반려 사유를 입력하세요 (1~500자)');
    if (!reason) return;
    setActionBusy(ownerUid); setError(null);
    try {
      await httpsCallable(getFunctions(), 'rejectTournamentApplication')({
        formId, ownerUid, rejectReason: reason,
        clientRequestId: `admin-reject-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      // V70 QA 수정 #3 (2026-10-21) — 성공 시 상세 팝업 자동 닫기 + 목록 즉시 갱신. 실패 시 팝업 유지.
      setSelectedApp(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError((err as { message?: string })?.message || '반려 실패');
    } finally {
      setActionBusy(null);
    }
  }

  function tsMs(ts: { toMillis?: () => number; _seconds?: number } | null | undefined): number {
    if (!ts) return 0;
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts._seconds === 'number') return ts._seconds * 1000;
    return 0;
  }
  function fmt(ms: number): string {
    if (!ms) return '-';
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  const statusTone = (s: AppRow['status']): string =>
    ({ submitted: '#2563eb', approved: '#059669', rejected: '#dc2626', cancelled: '#6b7280' } as const)[s];

  return (
    <section style={{ marginBottom: 16, padding: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>신청현황</h3>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <label>상태:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 4 }}>
            <option value="all">전체</option>
            <option value="submitted">검토 대기</option>
            <option value="approved">승인</option>
            <option value="rejected">반려</option>
            <option value="cancelled">취소</option>
          </select>
          <button
            style={{ padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
            onClick={() => setRefreshKey((k) => k + 1)}
          >새로고침</button>
        </div>
      </div>

      {aggregate && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 11 }}>
          <span style={aggPill('#eff6ff', '#1e40af')}>총 {aggregate.total}건</span>
          <span style={aggPill('#dbeafe', '#1e40af')}>검토 대기 {aggregate.submitted}</span>
          <span style={aggPill('#d1fae5', '#065f46')}>승인 {aggregate.approved}</span>
          <span style={aggPill('#fee2e2', '#991b1b')}>반려 {aggregate.rejected}</span>
          <span style={aggPill('#f1f5f9', '#334155')}>취소 {aggregate.cancelled}</span>
          <span style={aggPill('#fef3c7', '#92400e')}>참여 UID {aggregate.uniqueParticipants}명</span>
          <span style={aggPill('#ede9fe', '#5b21b6')}>팀 {aggregate.teamCount} · 개인 {aggregate.soloCount}</span>
        </div>
      )}

      {error && <div style={{ marginTop: 10, padding: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 4, fontSize: 12 }}>{error}</div>}

      {loading && <div style={{ marginTop: 10, padding: 16, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>로딩 중…</div>}

      {!loading && applications.length === 0 && (
        <div style={{ marginTop: 10, padding: 16, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>조건에 맞는 신청이 없습니다.</div>
      )}

      {!loading && applications.length > 0 && (
        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={th}>구분</th>
                <th style={th}>신청자/팀장</th>
                <th style={th}>팀명</th>
                <th style={th}>팀원 수</th>
                <th style={th}>상태</th>
                <th style={th}>신청일</th>
                <th style={th}>조치</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((a) => {
                const teamName = teamNameOf(a);
                const memberCount = a.mode === 'team' ? (a.memberUids.length + 1) : 1;
                return (
                  <tr
                    key={a.applicationId}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onClick={() => setSelectedApp(a)}
                  >
                    <td style={td}><span style={aggPill(a.mode === 'solo' ? '#f1f5f9' : '#ede9fe', '#334155')}>{a.mode === 'solo' ? '개인' : '팀'}</span></td>
                    <td style={td}>
                      <div>{displayUid(a.ownerUid, a.ownerDisplayName)}</div>
                      {uidToEmp.get(a.ownerUid)?.orgUnitName && (
                        <div style={{ color: '#94a3b8', fontSize: 11 }}>{uidToEmp.get(a.ownerUid)!.orgUnitName}</div>
                      )}
                    </td>
                    <td style={td}>{teamName || (a.mode === 'solo' ? '-' : '(미기재)')}</td>
                    <td style={td}>{memberCount}명</td>
                    <td style={td}>
                      <span style={{ color: statusTone(a.status), fontWeight: 700 }}>
                        {({ submitted: '검토 대기', approved: '승인', rejected: '반려', cancelled: '취소' } as const)[a.status]}
                      </span>
                      {a.status === 'rejected' && a.rejectReason && (
                        <div style={{ color: '#991b1b', fontSize: 11, marginTop: 2 }}>사유: {a.rejectReason}</div>
                      )}
                    </td>
                    <td style={td}>{fmt(tsMs(a.submittedAt))}</td>
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      {approvalMode === 'manual' && a.status === 'submitted' ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            style={{ padding: '4px 10px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                            onClick={() => handleApprove(a.ownerUid)}
                            disabled={actionBusy === a.ownerUid}
                          >승인</button>
                          <button
                            style={{ padding: '4px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                            onClick={() => handleReject(a.ownerUid)}
                            disabled={actionBusy === a.ownerUid}
                          >반려</button>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* V70 Phase B UX 개선 — 신청 상세 모달 (지시 §3) */}
      {selectedApp && (
        <ApplicationDetailModal
          app={selectedApp}
          form={form}
          displayUid={displayUid}
          getOrgUnit={(uid) => uidToEmp.get(uid)?.orgUnitName || null}
          onClose={() => setSelectedApp(null)}
          onApprove={approvalMode === 'manual' && selectedApp.status === 'submitted' ? () => handleApprove(selectedApp.ownerUid) : undefined}
          onReject={approvalMode === 'manual' && selectedApp.status === 'submitted' ? () => handleReject(selectedApp.ownerUid) : undefined}
          actionBusy={actionBusy === selectedApp.ownerUid}
        />
      )}
    </section>
  );
}

const aggPill = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '3px 10px', borderRadius: 10,
  background: bg, color, fontWeight: 600,
});
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', color: '#374151', fontWeight: 700 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };

// ─────────────────────────────────────────────────────────────────────────────
// V70 Phase B UX 개선 (2026-10-20) — 신청 상세 모달 (지시 §3)
//   - 양식 fields 순서대로 질문/답변 표시
//   - 팀 공통(teamAnswers) + 멤버별(memberAnswers) 구분
//   - 메타: 신청일, 수정일, 상태, 반려 사유, 승인자/반려자
//   - UID 는 노출하지 않음 (displayUid 로 변환)
// ─────────────────────────────────────────────────────────────────────────────

function ApplicationDetailModal({
  app, form, displayUid, getOrgUnit, onClose, onApprove, onReject, actionBusy,
}: {
  app: AppRow;
  form?: ApplyForm;
  displayUid: (uid: string, fallback?: string | null) => string;
  getOrgUnit: (uid: string) => string | null;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  actionBusy: boolean;
}) {
  const activeFields = (form?.fields || []).filter((f: Field) => !f.deleted).sort((a: Field, b: Field) => a.order - b.order);
  const teamFields = activeFields.filter((f: Field) => f.scope === 'team');
  const memberFields = activeFields.filter((f: Field) => f.scope === 'member');
  const allMemberUids = app.mode === 'team' ? [app.ownerUid, ...app.memberUids] : [app.ownerUid];

  function fmtTs(ts: { toMillis?: () => number; _seconds?: number } | null | undefined): string {
    if (!ts) return '-';
    const ms = typeof ts.toMillis === 'function' ? ts.toMillis() : (ts._seconds ? ts._seconds * 1000 : 0);
    if (!ms) return '-';
    return new Date(ms).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
  }
  function fmtAnswer(v: string | string[] | undefined): string {
    if (v == null) return '(미기재)';
    if (Array.isArray(v)) return v.length > 0 ? v.join(', ') : '(미기재)';
    return v.trim().length > 0 ? v : '(미기재)';
  }
  const statusLabelMap = { submitted: '검토 대기', approved: '승인', rejected: '반려', cancelled: '취소' } as const;
  const statusColorMap = { submitted: '#2563eb', approved: '#059669', rejected: '#dc2626', cancelled: '#6b7280' } as const;

  return (
    <div style={detailOverlay} onClick={onClose}>
      <div style={detailModal} onClick={(e) => e.stopPropagation()}>
        <div style={detailHeader}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>신청 상세</h3>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {app.mode === 'solo' ? '개인' : `팀 (${allMemberUids.length}명)`}
              {' · '}신청일 {fmtTs(app.submittedAt)}
              {app.updatedAt && app.submittedAt && fmtTs(app.updatedAt) !== fmtTs(app.submittedAt) && (
                <>{' · '}수정일 {fmtTs(app.updatedAt)}</>
              )}
            </div>
          </div>
          <button style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ ...aggPill(statusColorMap[app.status] + '22', statusColorMap[app.status]), fontSize: 12, fontWeight: 700, padding: '4px 12px' }}>
            {statusLabelMap[app.status]}
          </span>
          {app.status === 'approved' && app.approvedAt && (
            <span style={{ fontSize: 11, color: '#065f46' }}>승인 {fmtTs(app.approvedAt)} · {app.approvedByName || '-'}</span>
          )}
          {app.status === 'rejected' && (
            <>
              <span style={{ fontSize: 11, color: '#991b1b' }}>반려 {fmtTs(app.rejectedAt)} · {app.rejectedByName || '-'}</span>
              {app.rejectReason && (
                <div style={{ width: '100%', color: '#991b1b', fontSize: 12, marginTop: 4 }}>
                  <strong>반려 사유:</strong> {app.rejectReason}
                </div>
              )}
            </>
          )}
          {app.status === 'cancelled' && app.cancelledAt && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>취소 {fmtTs(app.cancelledAt)}</span>
          )}
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {/* 신청자 (팀장) 정보 */}
          <section style={{ marginBottom: 16 }}>
            <h4 style={detailSectionTitle}>{app.mode === 'solo' ? '신청자' : '팀장'}</h4>
            <div style={{ padding: 10, background: '#f8fafc', borderRadius: 6, fontSize: 13 }}>
              <div>{displayUid(app.ownerUid, app.ownerDisplayName)}</div>
              {getOrgUnit(app.ownerUid) && (
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{getOrgUnit(app.ownerUid)}</div>
              )}
            </div>
          </section>

          {/* 팀 공통 항목 */}
          {teamFields.length > 0 && (
            <section style={{ marginBottom: 16 }}>
              <h4 style={detailSectionTitle}>{app.mode === 'solo' ? '제출 항목 (공통)' : '팀 공통 항목'}</h4>
              {teamFields.map((f: Field) => (
                <div key={f.fieldId} style={detailQaRow}>
                  <div style={detailQaLabel}>
                    {(f.label && f.label.trim()) || '(라벨 없음)'}{f.required && <span style={{ color: '#dc2626' }}> *</span>}
                  </div>
                  <div style={detailQaValue}>{fmtAnswer(f.fieldId ? app.teamAnswers?.[f.fieldId] : undefined)}</div>
                </div>
              ))}
            </section>
          )}

          {/* 구성원 개별 항목 */}
          {memberFields.length > 0 && (
            <section>
              <h4 style={detailSectionTitle}>구성원 개별 항목</h4>
              {allMemberUids.map((u, i) => (
                <div key={u} style={{ marginBottom: 10, padding: 10, border: '1px solid #e5e7eb', borderRadius: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                    {/* V70 QA 추가수정 (2026-10-20) — 지시 §2: 번호(index) 노출 금지. 팀원 식별은 이름으로. */}
                    {i === 0 ? '팀장' : '팀원'} · {displayUid(u)}
                    {getOrgUnit(u) && (
                      <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>({getOrgUnit(u)})</span>
                    )}
                  </div>
                  {memberFields.map((f: Field) => (
                    <div key={f.fieldId} style={detailQaRow}>
                      <div style={detailQaLabel}>
                        {(f.label && f.label.trim()) || '(라벨 없음)'}{f.required && <span style={{ color: '#dc2626' }}> *</span>}
                      </div>
                      <div style={detailQaValue}>{fmtAnswer(f.fieldId ? app.memberAnswers?.[u]?.[f.fieldId] : undefined)}</div>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          )}
        </div>

        {(onApprove || onReject) && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {onReject && (
              <button
                style={{ padding: '8px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                onClick={onReject}
                disabled={actionBusy}
              >반려</button>
            )}
            {onApprove && (
              <button
                style={{ padding: '8px 14px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                onClick={onApprove}
                disabled={actionBusy}
              >승인</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const detailOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const detailModal: React.CSSProperties = {
  background: '#fff', borderRadius: 10, width: '100%', maxWidth: 720,
  maxHeight: '92vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
};
const detailHeader: React.CSSProperties = {
  padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
};
const detailSectionTitle: React.CSSProperties = {
  margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#1e293b',
  paddingBottom: 4, borderBottom: '1px solid #e5e7eb',
};
const detailQaRow: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, padding: '6px 0', fontSize: 13,
};
const detailQaLabel: React.CSSProperties = { color: '#475569', fontWeight: 600 };
const detailQaValue: React.CSSProperties = { color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };

// ─────────────────────────────────────────────────────────────────────────────
// V70 Phase B UX 개선 (2026-10-20) — 양식 대표 이미지 업로드 섹션 (지시 §1)
//   - uploadTournamentApplyFormImage (Base64 1-step) 호출
//   - 업로드 성공 → 즉시 updateTournamentApplyForm 로 coverImageUrl/coverImagePath 저장
//   - 삭제: updateTournamentApplyForm({coverImageUrl:null, coverImagePath:null})
//           → BE forms.ts 가 자동으로 Storage 이전 파일 삭제
//   - 5MB 제한, image/jpeg/png/gif/webp
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_IMG_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMG_BYTES = 5 * 1024 * 1024;

function CoverImageSection({
  formId, coverImageUrl, coverImagePath, expectedVersion, onUpdated,
}: {
  formId: string;
  coverImageUrl: string | null;
  coverImagePath: string | null;
  expectedVersion: number;
  onUpdated: (next: { coverImageUrl: string | null; coverImagePath: string | null; version: number }) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  async function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      if (!ALLOWED_IMG_TYPES.includes(file.type)) {
        throw new Error(`지원하지 않는 이미지 형식 (${file.type}). JPEG/PNG/GIF/WebP 만 가능`);
      }
      if (file.size > MAX_IMG_BYTES) {
        throw new Error(`파일 크기 초과 (최대 ${MAX_IMG_BYTES / 1024 / 1024}MB)`);
      }
      setUploading(true);
      const fileBase64 = await fileToBase64(file);
      const fns = getFunctions();
      // V70 Phase B UX 개선 (보완판) — upload CF 가 Storage + Firestore 반영 둘 다 처리
      //   실패 시 BE 가 자동 rollback → 기존 이미지가 유지됨 (FE 측 추가 작업 不要)
      const upFn = httpsCallable<any, { success: true; imageUrl: string; imagePath: string; version: number }>(
        fns,
        'uploadTournamentApplyFormImage',
      );
      const upRes = await upFn({
        formId,
        fileBase64,
        contentType: file.type,
        fileName: file.name,
        expectedVersion,
      });
      onUpdated({
        coverImageUrl: upRes.data.imageUrl,
        coverImagePath: upRes.data.imagePath,
        version: upRes.data.version,
      });
    } catch (err) {
      setError((err as { message?: string })?.message || '이미지 업로드 실패 (기존 이미지 유지)');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onDelete() {
    if (!coverImagePath) return;
    if (!window.confirm('대표 이미지를 삭제하시겠습니까?')) return;
    setError(null); setUploading(true);
    try {
      const fns = getFunctions();
      // V70 Phase B UX 개선 (보완판) — delete CF 가 formId 소유권 검증 + Firestore null + Storage 삭제
      const delFn = httpsCallable<any, { success: true; version: number }>(fns, 'deleteTournamentApplyFormImage');
      const delRes = await delFn({ formId, expectedVersion });
      onUpdated({ coverImageUrl: null, coverImagePath: null, version: delRes.data.version });
    } catch (err) {
      setError((err as { message?: string })?.message || '이미지 삭제 실패');
    } finally {
      setUploading(false);
    }
  }

  return (
    <section style={{ marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>대표 이미지 (선택)</div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{
          width: 160, height: 100, border: '1px solid #e5e7eb', borderRadius: 6,
          background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {coverImageUrl ? (
            <img src={coverImageUrl} alt="대표 이미지" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>미등록</div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_IMG_TYPES.join(',')}
            onChange={onPickFile}
            disabled={uploading}
            style={{ fontSize: 12 }}
          />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            JPEG / PNG / GIF / WebP · 최대 5MB · 양식 수정과 함께 즉시 저장
          </div>
          {coverImageUrl && (
            <button
              type="button"
              onClick={onDelete}
              disabled={uploading}
              style={{ marginTop: 8, padding: '4px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
            >
              이미지 삭제
            </button>
          )}
          {uploading && <div style={{ marginTop: 6, fontSize: 11, color: '#2563eb' }}>처리 중…</div>}
          {error && <div style={{ marginTop: 6, padding: 6, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 4, fontSize: 11 }}>{error}</div>}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// V70 Phase C — Admin 대진표 탭 (룰 작성 / 추첨 / 게시 / 세트 입력 / 정정)
// ─────────────────────────────────────────────────────────────────────────────

type BracketPreview = {
  approvedCount: number;
  pendingCount: number;
  recommendedStartRoundSize: number;
  participants: Array<{
    applicationId: string; ownerUid: string;
    displayName: string; teamName: string | null; orgUnitName: string | null;
    mode: 'solo' | 'team'; memberUids: string[];
  }>;
  // V70 QA 수정 #1 (2026-10-21) — 기존 bracket 복원 정보. null 이면 아직 생성 안 됨.
  existingBracket: {
    bracketId: string;
    status: BrStatus;
    startRoundSize: number;
    totalRounds: number;
    version: number;
  } | null;
};

type AdminBracket = PublicBracketView & {
  participants: PublicParticipantView[];
  seed: string | null;
};

type AdminMatch = PublicMatchView;

// V70 QA 추가 수정 #4 (2026-10-21) — preview.participants 중 team 모드가 하나라도 있으면 단체전으로 간주.
function isTeamModePreview(p: BracketPreview): boolean {
  return (p.participants || []).some((x) => x.mode === 'team');
}

function BracketTab({
  formId, formTitle, applyEndMs, publicStatus,
}: { formId: string; formTitle: string; applyEndMs: number; publicStatus: FormStatus }) {
  const [preview, setPreview] = useState<BracketPreview | null>(null);
  const [bracket, setBracket] = useState<AdminBracket | null>(null);
  const [matches, setMatches] = useState<AdminMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // 룰 입력 state
  const [ruleStartSize, setRuleStartSize] = useState<number>(0);
  const [ruleBOs, setRuleBOs] = useState<BestOfMode[]>([]);
  // V70 QA 리오픈 NN2 재수정 (2026-10-23) — 룰 수정 모드 플래그.
  //   true 이면 bracket 뷰 대신 룰 선택 스텝 UI 를 노출 (룰 저장 → false 로 복귀).
  const [ruleEditMode, setRuleEditMode] = useState(false);

  // 세트 입력 모달
  const [selectedMatch, setSelectedMatch] = useState<AdminMatch | null>(null);
  // V70 QA 신규 기능 #1·#3 (2026-10-22) — 매치 메타(일정/중계) 설정 모달
  const [metaMatch, setMetaMatch] = useState<AdminMatch | null>(null);
  // 정정 모달
  const [correctionMatch, setCorrectionMatch] = useState<AdminMatch | null>(null);
  const [correctionPreview, setCorrectionPreview] = useState<any | null>(null);
  const [correctionReason, setCorrectionReason] = useState<string>('');

  const periodOver = publicStatus === 'closed' || publicStatus === 'archived' || Date.now() > applyEndMs;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const fns = getFunctions();
        const pv = await httpsCallable<{ sourceFormId: string }, BracketPreview>(fns, 'getBracketPreview')({ sourceFormId: formId });
        if (cancelled) return;
        setPreview(pv.data);
        if (!ruleStartSize && pv.data.recommendedStartRoundSize) {
          setRuleStartSize(pv.data.recommendedStartRoundSize);
          const totalRounds = Math.log2(pv.data.recommendedStartRoundSize);
          setRuleBOs(Array.from({ length: totalRounds }, () => 1 as BestOfMode));
        }
        // V70 QA 수정 #1 (2026-10-21) — 기존 bracket 복원.
        //   탭 이동/페이지 새로고침/관리자 재접속 시 preview 응답의 existingBracket 로 getBracketWithMatches 자동 호출.
        //   재추첨 X, reset X. 데이터 보존 최우선. (fetchBracket 은 getBracketWithMatches 만 호출)
        if (pv.data.existingBracket && pv.data.existingBracket.bracketId) {
          try {
            const r = await httpsCallable<{ bracketId: string }, { bracket: any; matches: any[] }>(fns, 'getBracketWithMatches')({ bracketId: pv.data.existingBracket.bracketId });
            if (cancelled) return;
            setBracket(r.data.bracket as AdminBracket);
            setMatches(r.data.matches as AdminMatch[]);
          } catch (e: any) {
            if (!cancelled) setErr(e?.message || String(e));
          }
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, refreshKey]);

  const fetchBracket = async (bracketId: string) => {
    try {
      const fns = getFunctions();
      const r = await httpsCallable<{ bracketId: string }, { bracket: any; matches: any[] }>(fns, 'getBracketWithMatches')({ bracketId });
      setBracket(r.data.bracket as AdminBracket);
      setMatches(r.data.matches as AdminMatch[]);
    } catch (e: any) {
      setErr(e?.message || String(e));
    }
  };

  // V70 QA 리오픈 수정 #5 (2026-10-22) — 룰 저장/추첨 직전 preview 재조회.
  //   handleSaveRule, handleDraw(최초 추첨) 가 공통으로 사용. BE 거부 로직은 그대로 유지.
  //   Preview 갱신 실패 시 저장/추첨 진행 금지.
  const refreshPreviewOrFail = async (): Promise<BracketPreview | null> => {
    try {
      const fns = getFunctions();
      const pv = await httpsCallable<{ sourceFormId: string }, BracketPreview>(fns, 'getBracketPreview')({ sourceFormId: formId });
      if (!pv.data) {
        setErr('Preview 조회 실패 — 네트워크 확인 후 다시 시도하세요.');
        return null;
      }
      setPreview(pv.data);
      return pv.data;
    } catch (e: any) {
      setErr(`Preview 갱신 실패: ${e?.message || String(e)}`);
      return null;
    }
  };

  const handleSaveRule = async () => {
    if (!preview || !ruleStartSize) return;
    setBusy(true); setErr(null);
    try {
      // V70 QA 리오픈 수정 #5 — 최신 preview 재조회 후 pending/승인자 수 재검증
      const fresh = await refreshPreviewOrFail();
      if (!fresh) return;
      if (fresh.pendingCount > 0) {
        setErr(`승인 대기 중인 신청 ${fresh.pendingCount}건을 먼저 처리하세요. (룰 저장 차단)`);
        return;
      }
      if (fresh.approvedCount < 2) {
        setErr('승인 참가자가 2명 미만입니다. 대진표를 생성할 수 없습니다.');
        return;
      }
      const totalRounds = Math.log2(ruleStartSize);
      const roundConfig = Array.from({ length: totalRounds }, (_, i) => ({
        roundNo: i + 1, bestOf: ruleBOs[i] || 1,
      }));
      const fns = getFunctions();
      const r = await httpsCallable<any, { bracketId: string; version: number; status: BrStatus }>(fns, 'createOrUpdateBracketRule')({
        sourceFormId: formId, type: 'single-elimination',
        startRoundSize: ruleStartSize, roundConfig,
        clientRequestId: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      await fetchBracket(r.data.bracketId);
      // V70 QA 리오픈 NN2 재수정 (2026-10-23) — 룰 저장 성공 시 ruleEditMode 종료 → draft bracket 복귀.
      setRuleEditMode(false);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally { setBusy(false); }
  };

  const handleDraw = async () => {
    if (!bracket) return;
    setBusy(true); setErr(null);
    try {
      // V70 QA 리오픈 수정 #6 — 최초 추첨(draft) 시 최신 preview 재조회로 approvedCount/pendingCount 재검증.
      //   재추첨(drawn)은 BE drawBracket 가 데이터 보존 하에 거부/허용 결정 — FE 가드 불필요.
      if (bracket.status === 'draft') {
        const fresh = await refreshPreviewOrFail();
        if (!fresh) return;
        if (fresh.pendingCount > 0) {
          alert(`승인 대기 신청 ${fresh.pendingCount}건이 있어 추첨이 차단됩니다. 승인/반려 처리 후 다시 시도하세요.`);
          return;
        }
        if (fresh.approvedCount < 2) {
          alert('승인 참가자가 2명 미만입니다. 대진이 성립하지 않아 추첨할 수 없습니다.');
          return;
        }
      }
      if (!window.confirm('랜덤 추첨을 실행하시겠습니까? 재추첨 시 이전 추첨 결과는 교체됩니다 (이력은 보존).')) return;
      const fns = getFunctions();
      await httpsCallable(fns, 'drawBracket')({ bracketId: bracket.bracketId, clientRequestId: `draw-${Date.now()}` });
      await fetchBracket(bracket.bracketId);
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  // V70 QA 리오픈 NN2 재수정 (2026-10-23) — [룰 수정] 클릭 시 추첨 결과 초기화 + **룰 선택 스텝으로 복귀**.
  //   이전엔 resetBracketDraw 만 호출 → 추첨만 리셋되고 룰 유지 → 사용자가 룰 변경 UI 접근 불가.
  //   재수정: resetBracketDraw → ruleEditMode=true → 상단에서 룰 선택 섹션 렌더 (현재 룰값 pre-fill)
  //   사용자가 룰 저장 (createOrUpdateBracketRule) → ruleEditMode=false → draft bracket 복귀 → "랜덤 추첨" 가능.
  const handleReset = async () => {
    if (!bracket) return;
    if (!window.confirm('룰 수정 모드로 전환합니다. 추첨 결과가 초기화되고 룰 선택 스텝으로 돌아갑니다. 진행하시겠습니까?')) return;
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'resetBracketDraw')({ bracketId: bracket.bracketId, clientRequestId: `reset-${Date.now()}` });
      // 현재 bracket 의 룰값을 pre-fill → 사용자가 수정하기 쉽도록
      setRuleStartSize(bracket.startRoundSize);
      setRuleBOs(bracket.roundConfig.map((rc) => rc.bestOf as BestOfMode));
      // preview 재조회 (승인자 수 등 최신화)
      const pv = await httpsCallable<{ sourceFormId: string }, BracketPreview>(fns, 'getBracketPreview')({ sourceFormId: formId });
      setPreview(pv.data);
      await fetchBracket(bracket.bracketId); // status=draft 로 바뀐 bracket 복원 (ruleEditMode UI 에서 다시 저장 후 복귀)
      setRuleEditMode(true);
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const handlePublish = async () => {
    if (!bracket) return;
    if (!window.confirm('⚠ 게시 후 재추첨 불가합니다. 게시하시겠습니까?')) return;
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'publishBracket')({ bracketId: bracket.bracketId, clientRequestId: `pub-${Date.now()}` });
      await fetchBracket(bracket.bracketId);
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const handleCancel = async () => {
    if (!bracket) return;
    const reason = window.prompt('취소 사유 (1-500자):');
    if (!reason) return;
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'cancelBracket')({ bracketId: bracket.bracketId, reason, clientRequestId: `cancel-${Date.now()}` });
      await fetchBracket(bracket.bracketId);
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  // V70 QA 추가 수정 #2-b (2026-10-22) — 취소된 대진표 재생성.
  //   기존 cancelled bracket 는 Firestore 에 보존 (이력). 신규 bracketId 로 새 bracket 생성.
  //   재스냅샷은 현재 승인자 기준. 룰은 복사. 활성 bracket 중복 금지.
  // V70 QA 리오픈 #2 (2026-10-22) — 재생성 버튼 피드백 강화.
  //   로딩 상태(busy) + 성공 안내(successMsg) + 중복 클릭 차단(busy guard) + 에러 메시지.
  //   버튼 클릭 → confirm → 서버 호출 → 새 bracketId 로 전환.
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // V70 QA 리오픈 R3 재수정 (2026-10-22) — 재생성 정책을 **실제로 새 bracket 을 생성**하도록 변경.
  //   이전 수정(auto-fetch-on-already-exists)은 "조회"였고 "재생성"이 아니었음 — 사용자 요구 미충족.
  //   변경 정책:
  //     - cancelled → 직접 recreateCancelledBracket 호출 (BE 로직 동일)
  //     - draft/drawn/published/in-progress → 상태별 경고 후 cancelBracket + recreateCancelledBracket **체인**
  //     - completed → 재생성 금지 (경기 종료)
  //   금지 조건 (사용자 verbatim):
  //     - "기존 대진표와 감사 이력은 보존하세요." → cancelBracket 은 상태 전환만 (삭제 X), bracketHistory 는 유지
  //     - "기존 진행 결과를 무단 삭제하거나 초기화하지 마세요." → 사용자 명시 confirm 전엔 cancel 금지
  //     - 신규 bracketId 발급 (recreateCancelledBracket 가 보장), 새 roundConfig/participants 재스냅샷
  //   각 상태별 confirm 메시지 분리 — 사용자가 영향 범위를 명확히 인지 후 진행.
  const cancelAndRecreate = async (currentBracketId: string): Promise<string | null> => {
    const fns = getFunctions();
    // V70 QA 리오픈 NNN8 (2026-10-23) — 자동 취소 사유를 사용자용 문구로 변경.
    //   이전엔 "(admin recreate flow)" 내부 marker 가 사용자 화면 (BracketDetailPage) 에 그대로 노출됨.
    //   현재: 내부 marker 제거, 사용자 친화적 문구만 저장. 신규 cancel 부터 적용.
    //   기존 저장 데이터는 BracketDetailPage 측에서 marker 를 sanitize 하여 노출.
    await httpsCallable(fns, 'cancelBracket')({
      bracketId: currentBracketId,
      reason: '재생성을 위한 자동 취소',
      clientRequestId: `cancel-for-recreate-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    });
    // 2단계: 방금 cancel 된 bracket 을 소스로 재생성 (새 bracketId)
    const r = await httpsCallable<any, { bracketId: string; status: BrStatus; reused?: boolean }>(fns, 'recreateCancelledBracket')({
      cancelledBracketId: currentBracketId,
      clientRequestId: `recreate-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    });
    return r.data.bracketId;
  };

  const handleRecreate = async () => {
    if (!bracket) return;
    if (busy) return;
    // V70 QA 리오픈 NN1 재수정 (2026-10-23) — 재생성 허용 상태를 cancelled/published/in-progress 로 한정.
    //   draft/drawn 은 "재추첨"/"룰 수정"으로 처리, completed/archived 는 재생성 불가.
    if (bracket.status !== 'cancelled' && bracket.status !== 'published' && bracket.status !== 'in-progress') {
      setErr(`${bracket.status} 상태에서는 재생성할 수 없습니다. (cancelled/published/in-progress 만 허용)`);
      return;
    }

    // 상태별 confirm 분기
    const confirmMsg = (() => {
      switch (bracket.status) {
        case 'cancelled':
          return '취소된 대진표를 재생성합니다. 기존 cancelled 대진표는 이력으로 보존되고 신규 bracketId 로 새 대진표가 만들어집니다. 진행할까요?';
        case 'published':
          return '게시된 대진표입니다. 현재 bracket 은 자동 취소되고(이력 보존) 신규 bracketId 로 재생성됩니다.\n저장된 경기 결과(완료 매치/세트)는 cancelled bracket 에 그대로 유지됩니다. 진행할까요?';
        case 'in-progress':
          return '⚠ 경기 진행 중입니다. 현재 bracket 은 자동 취소되고(이력 보존) 신규 bracketId 로 재생성됩니다.\n진행 중인 경기 결과(완료 매치/세트)는 cancelled bracket 에 보존되지만, 새 bracket 은 모든 매치가 초기 상태로 생성됩니다.\n정말 진행할까요?';
        default:
          return `현재 상태(${bracket.status}) 에서 재생성합니다. 진행할까요?`;
      }
    })();
    if (!window.confirm(confirmMsg)) return;

    setBusy(true); setErr(null); setSuccessMsg(null);
    try {
      let newBracketId: string | null = null;
      if (bracket.status === 'cancelled') {
        // 직접 recreate
        const fns = getFunctions();
        const r = await httpsCallable<any, { bracketId: string; status: BrStatus; reused?: boolean }>(fns, 'recreateCancelledBracket')({
          cancelledBracketId: bracket.bracketId,
          clientRequestId: `recreate-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
        newBracketId = r.data.bracketId;
      } else {
        // 활성 상태 → cancel + recreate 체인
        newBracketId = await cancelAndRecreate(bracket.bracketId);
      }
      if (!newBracketId) throw new Error('신규 bracketId 를 얻지 못했습니다.');
      await fetchBracket(newBracketId);
      setRefreshKey((k) => k + 1);
      setSuccessMsg(`대진표 재생성 완료. 신규 bracketId=${newBracketId} (이전 bracket 은 cancelled 로 이력 보존).`);
      setTimeout(() => setSuccessMsg(null), 7000);
    } catch (e: any) {
      // V70 QA 리오픈 R3 재수정 (2026-10-22) — already-exists 복구:
      //   첫 호출 성공 but 응답 유실 → 재클릭 시 BE 가 already-exists throw.
      //   이전 수정(auto-fetch existing)은 "조회"였고 재생성이 아니었음 — 사용자 지적.
      //   현재: 사용자에게 두 선택지 제시:
      //     (a) 기존 bracket 그대로 열기 (조회)
      //     (b) 기존을 취소하고 다시 재생성
      //   둘 다 사용자 explicit confirm 필요 — 무단 삭제/덮어쓰기 금지.
      if (e?.code === 'already-exists') {
        try {
          const fns = getFunctions();
          const pv = await httpsCallable<{ sourceFormId: string }, any>(fns, 'getBracketPreview')({ sourceFormId: formId });
          const existing = pv.data?.existingBracket;
          if (existing && existing.bracketId && existing.bracketId !== bracket.bracketId) {
            // 사용자 명시 선택
            const choice = window.confirm(
              `동일 양식에 활성 bracket (status=${existing.status}, id=${existing.bracketId}) 이 존재합니다.\n\n` +
              `[확인] = 기존 bracket 을 취소하고 다시 재생성합니다 (신규 bracketId 발급, 이전 bracket 은 이력 보존).\n` +
              `[취소] = 기존 활성 bracket 을 그대로 열어서 조회만 합니다.`
            );
            if (choice) {
              // 기존 cancel + 재생성
              const newId = await cancelAndRecreate(existing.bracketId);
              if (newId) {
                await fetchBracket(newId);
                setRefreshKey((k) => k + 1);
                setSuccessMsg(`재생성 완료. 신규 bracketId=${newId}.`);
                setTimeout(() => setSuccessMsg(null), 7000);
                return;
              }
            } else {
              // 조회만
              await fetchBracket(existing.bracketId);
              setRefreshKey((k) => k + 1);
              setSuccessMsg(`기존 활성 대진표(${existing.status})를 조회만 했습니다 — 재생성은 수행하지 않음. (bracketId=${existing.bracketId})`);
              setTimeout(() => setSuccessMsg(null), 7000);
              return;
            }
          }
        } catch { /* 조회 실패 — 아래 에러 메시지 노출 */ }
      }
      setErr(`재생성 실패: ${e?.message || String(e)}`);
    } finally { setBusy(false); }
  };

  const handleRecordSet = async (match: AdminMatch, setNo: number, winnerSide: BrSide) => {
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'recordMatchSet')({
        matchId: match.matchId, setNo, winnerSide,
        clientRequestId: `rec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      if (bracket) await fetchBracket(bracket.bracketId);
      setSelectedMatch(null);
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const openCorrection = async (match: AdminMatch, newWinner: BrSide) => {
    setCorrectionMatch(match);
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      const r = await httpsCallable<any, any>(fns, 'previewCorrection')({ matchId: match.matchId, newWinnerSide: newWinner });
      setCorrectionPreview({ ...r.data, newWinner });
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  const applyCorrection = async () => {
    if (!correctionMatch || !correctionPreview || !correctionReason.trim()) return;
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'applyCorrection')({
        matchId: correctionMatch.matchId, newWinnerSide: correctionPreview.newWinner,
        reason: correctionReason, impactAcknowledged: true,
        clientRequestId: `corr-${Date.now()}`,
      });
      if (bracket) await fetchBracket(bracket.bracketId);
      setCorrectionMatch(null); setCorrectionPreview(null); setCorrectionReason('');
    } catch (e: any) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  };

  if (!periodOver) {
    return (
      <section style={{ padding: 20, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8 }}>
        <strong>접수 기간이 종료된 후 대진표를 생성할 수 있습니다.</strong>
        <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
          현재 접수 상태: {publicStatus}
        </div>
      </section>
    );
  }
  // V70 QA 리오픈 #8 (2026-10-22) — 보관(archived) 양식: 조회는 허용, 수정만 차단.
  //   이전엔 BracketTab 전체 숨김 → 완료된 대진표/결과/우승자가 안 보이는 문제.
  //   현재: isReadOnlyArchived flag 로 BracketTreeView 는 노출, 룰 저장/추첨/수정 등 액션 버튼은 숨김.
  const isReadOnlyArchived = publicStatus === 'archived';

  return (
    <section style={{ padding: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>대진표 — {formTitle}</h3>
      {err && <div style={{ marginTop: 10, padding: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, fontSize: 12 }}>{err}</div>}
      {/* V70 QA 리오픈 #2 (2026-10-22) — 재생성 성공 피드백 */}
      {successMsg && <div style={{ marginTop: 10, padding: 8, background: '#d1fae5', color: '#065f46', border: '1px solid #86efac', borderRadius: 4, fontSize: 12 }}>✓ {successMsg}</div>}
      {loading && <div style={{ padding: 16, color: '#64748b' }}>로딩 중…</div>}
      {busy && <div style={{ padding: 8, background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: 4, fontSize: 12, marginTop: 10 }}>처리 중…</div>}

      {/* V70 QA 리오픈 NN2 (2026-10-23) — ruleEditMode 활성 시 bracket 뷰 대신 룰 선택 스텝 렌더.
            draft 상태 bracket 이 있어도 ruleEditMode=true 면 룰 편집 UI 노출.
            NNN3 (2026-10-23): "수정 취소" 버튼 제거 — 사용자 지시. 룰 저장만으로 모드 종료. */}
      {!loading && ruleEditMode && bracket && bracket.status === 'draft' && preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 10, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
            🛠 룰 수정 모드 — 추첨 결과가 초기화되었습니다. 룰을 수정하고 저장하면 다시 추첨할 수 있습니다.
          </div>
        </div>
      )}

      {!loading && (((!bracket || (ruleEditMode && bracket.status === 'draft')) && preview)) && (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 10, padding: 10, background: '#f1f5f9', borderRadius: 6, fontSize: 13 }}>
            승인 참가자 <strong>{preview.approvedCount}{isTeamModePreview(preview) ? '팀' : '명'}</strong>
            {preview.pendingCount > 0 && (
              <span style={{ color: '#dc2626', marginLeft: 8 }}>⚠ 승인 대기 {preview.pendingCount}건</span>
            )}
            {' · '}추천 라운드: <strong>{preview.recommendedStartRoundSize}강</strong>
          </div>
          {/* V70 QA 추가 수정 #8 (2026-10-21) — 승인 참가자 2명 미만일 때 대진표 생성 불가 안내. 결과 게시 등 별도 처리 유도. */}
          {preview.approvedCount < 2 && (
            <div style={{ marginBottom: 10, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, color: '#92400e', fontSize: 12 }}>
              승인 참가자가 2명 미만입니다. 대진이 성립하지 않으므로 대진표를 생성할 수 없습니다. (결과만 공지하거나 승인자를 추가하세요)
            </div>
          )}
          {/* V70 QA 리오픈 #4 (2026-10-22) — 안내 문구 간결화. 버튼 disabled 로 1차 피드백. */}
          {preview.pendingCount > 0 && (
            <div style={{ marginBottom: 10, padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 12 }}>
              승인 대기 중인 신청 {preview.pendingCount}건을 먼저 처리하세요.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12 }}>시작 라운드:</label>
            <select
              value={ruleStartSize || preview.recommendedStartRoundSize}
              onChange={() => { /* noop — 서버가 요구하는 값 고정 */ }}
              style={{ padding: 4, border: '1px solid #cbd5e1', borderRadius: 4 }}
              disabled
            >
              <option value={preview.recommendedStartRoundSize}>{preview.recommendedStartRoundSize}강 (고정)</option>
            </select>
            <span style={{ fontSize: 11, color: '#64748b' }}>※ 승인자 수 기준 가장 작은 2의 거듭제곱만 허용</span>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>라운드별 경기 방식</div>
            {ruleBOs.map((bo, i) => {
              const roundNo = i + 1;
              const totalRounds = ruleBOs.length;
              const remain = totalRounds - roundNo + 1;
              const label = remain === 1 ? '결승' : remain === 2 ? '준결승' : `${Math.pow(2, remain)}강`;
              return (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ minWidth: 60, fontSize: 12 }}>{label}:</span>
                  {/* V70 QA 추가 수정 #4 (2026-10-22) — BO1/3/5/7/9 라디오 + "N판 M승제" 라벨.
                       예: BO1 = 1판 1승제, BO3 = 3판 2승제, BO5 = 5판 3승제, BO7 = 7판 4승제, BO9 = 9판 5승제 */}
                  {[1, 3, 5, 7, 9].map((v) => (
                    <label key={v} style={{ fontSize: 12 }}>
                      <input
                        type="radio"
                        name={`bo-${i}`}
                        checked={bo === v}
                        onChange={() => setRuleBOs((prev) => { const nx = [...prev]; nx[i] = v as BestOfMode; return nx; })}
                      /> {v}판 {Math.ceil(v / 2)}승제
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
          {/* V70 QA 리오픈 #4 (2026-10-22) — disabled 상태 시각 표현 강화 (회색 배경 + cursor not-allowed). */}
          {(() => {
            const saveDisabled = busy || preview.approvedCount < 2 || preview.pendingCount > 0;
            return (
              <button
                style={{
                  ...brPrimaryBtn,
                  background: saveDisabled ? '#cbd5e1' : brPrimaryBtn.background,
                  color: saveDisabled ? '#64748b' : brPrimaryBtn.color,
                  cursor: saveDisabled ? 'not-allowed' : 'pointer',
                  opacity: saveDisabled ? 0.7 : 1,
                }}
                onClick={handleSaveRule}
                disabled={saveDisabled}
              >룰 저장 (bracket 생성)</button>
            );
          })()}
          {/* V70 QA 리오픈 #5 (2026-10-22) — 승인 참가자 0/1명이면 [결과만 공지] 버튼 노출 */}
          {preview.approvedCount < 2 && (
            <ResultAnnouncementSection
              sourceFormId={formId}
              preview={preview}
            />
          )}
        </div>
      )}

      {/* V70 QA 리오픈 NN2 재수정 (2026-10-23) — ruleEditMode 활성 시 bracket 뷰 숨김 (룰 편집 UI 가 상단에서 노출됨). */}
      {!loading && bracket && !(ruleEditMode && bracket.status === 'draft') && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ ...brPill, background: brStatusColor(bracket.status), color: '#fff' }}>{brStatusLabel(bracket.status)}</span>
            {bracket.participants.length > 0 && (
              <span style={{ fontSize: 12 }}>
                {/* V70 QA 추가 수정 #4 (2026-10-21) — 단체전 '참가자 N팀', 개인전 '참가자 N명' */}
                참가자 {bracket.participants.length}{bracket.participants.some((p: any) => p.mode === 'team') ? '팀' : '명'}, 부전승 {bracket.byeCount}건
              </span>
            )}
          </div>

          {/* V70 QA 리오픈 #8 (2026-10-22) — 보관(archived) 상태 양식: 수정 액션 전부 숨김, 뷰어는 아래 유지 */}
          {isReadOnlyArchived && (
            <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e', marginBottom: 10 }}>
              보관된 대회 — 대진표는 조회만 가능합니다. 수정/추첨/게시 등 액션은 비활성화됩니다.
            </div>
          )}
          {!isReadOnlyArchived && bracket.status === 'draft' && (
            <button style={brPrimaryBtn} onClick={handleDraw} disabled={busy}>랜덤 추첨 실행</button>
          )}
          {!isReadOnlyArchived && bracket.status === 'drawn' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={brPrimaryBtn} onClick={handleDraw} disabled={busy}>재추첨</button>
              <button style={brSecondaryBtn} onClick={handleReset} disabled={busy}>룰 수정 (reset)</button>
              <button style={{ ...brPrimaryBtn, background: '#059669' }} onClick={handlePublish} disabled={busy}>게시 (publish)</button>
            </div>
          )}
          {!isReadOnlyArchived && (bracket.status === 'published' || bracket.status === 'in-progress') && (
            <button style={brDangerBtn} onClick={handleCancel} disabled={busy}>대진표 취소</button>
          )}
          {/* V70 QA 리오픈 NN1 재수정 (2026-10-23) — 재생성 버튼은 **생성 및 게시된** 또는 **취소된** 상태에서만 노출.
                사용자 지시 verbatim: "대진표가 생성 및 게시된 상태에서만 대진표 재생성 버튼이 노출되어야 합니다."
                - draft/drawn (추첨 전 혹은 추첨 완료 미게시): 노출 X — "재추첨" 또는 "룰 수정"으로 처리
                - published/in-progress (게시됨): 노출 — 현재 취소 + 신규 생성 체인
                - cancelled: 노출 — 원래 recreate 흐름
                - completed/archived: 노출 X */}
          {!isReadOnlyArchived && (bracket.status === 'cancelled' || bracket.status === 'published' || bracket.status === 'in-progress') && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button style={brPrimaryBtn} onClick={handleRecreate} disabled={busy}>
                {bracket.status === 'cancelled' ? '대진표 재생성' : '대진표 재생성 (현재 취소 + 신규 생성)'}
              </button>
              <span style={{ fontSize: 11, color: '#64748b' }}>
                {bracket.status === 'cancelled'
                  ? '기존 cancelled 대진표는 이력으로 보존됩니다. 현재 승인자 기준으로 재스냅샷됩니다.'
                  : '현재 bracket 은 cancelled 상태로 이력 보존되고, 신규 bracketId 로 재생성됩니다. 상태별 영향은 confirm 에서 확인하세요.'}
              </span>
            </div>
          )}

          {bracket.participants.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <BracketTreeView
                bracket={bracket}
                matches={matches}
                showAdminActions={!isReadOnlyArchived}
                onMetaClick={isReadOnlyArchived ? undefined : (m) => setMetaMatch(m as any)}
                onMatchClick={isReadOnlyArchived ? undefined : (m) => {
                  if (m.status === 'completed') {
                    // V70 QA 리오픈 #9 (2026-10-22) — 완료 매치 클릭 시 세트별 재입력 모달 노출.
                    //   기존: window.confirm 으로 승자 바꾸기만 지원.
                    //   변경: 모달에서 기존 세트 결과 노출 + 각 세트 승자 toggle + 최종 승자 재계산 → applyCorrection.
                    setCorrectionMatch(m as any);
                    // openCorrection 는 유저가 모달에서 "미리보기 실행" 시 호출. 바로 호출 X.
                  } else if (m.status === 'ready' || m.status === 'in-progress') {
                    setSelectedMatch(m);
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      {selectedMatch && (
        <SetInputModal
          match={selectedMatch}
          bracket={bracket}
          onClose={() => setSelectedMatch(null)}
          onRecord={(setNo, winner) => handleRecordSet(selectedMatch, setNo, winner)}
          busy={busy}
        />
      )}

      {/* V70 QA 신규 기능 #1·#3 (2026-10-22) — 매치 메타(일정/중계) 설정 모달 */}
      {metaMatch && (
        <MatchMetaModal
          match={metaMatch}
          onClose={() => setMetaMatch(null)}
          onSaved={async () => {
            if (bracket) await fetchBracket(bracket.bracketId);
            setMetaMatch(null);
          }}
        />
      )}

      {/* V70 QA 리오픈 #9 (2026-10-22) — 완료 매치 세트별 재입력 모달.
            - 기존 세트 결과 표시 + 각 세트 승자 toggle
            - BO 룰 기반 승리 조건 안내 (ceil(bestOf/2))
            - 최종 승자 자동 재계산 → 변경 시 미리보기 (영향 매치) → 사유 입력 → 적용
            - 기존 correction API 재사용 (applyCorrection + previewCorrection) */}
      {correctionMatch && (
        <CorrectionModal
          match={correctionMatch}
          preview={correctionPreview}
          onPreview={openCorrection}
          onApply={applyCorrection}
          reason={correctionReason}
          setReason={setCorrectionReason}
          busy={busy}
          bracketId={bracket?.bracketId || ''}
          bracket={bracket}
          allMatches={matches}
          onReload={async () => { if (bracket) await fetchBracket(bracket.bracketId); }}
          onClose={() => { setCorrectionMatch(null); setCorrectionPreview(null); setCorrectionReason(''); }}
        />
      )}
    </section>
  );
}

// V70 QA 리오픈 #9 (2026-10-22) — 완료 매치 세트별 재입력 + 결과 정정 모달.
//   변경: BE `correctMatchSets` 신규 Callable 호출 → sets 배열 저장 + 승자 재계산 + 영향 매치 reset + 감사 이력.
//   기존 applyCorrection 은 승자만 변경 → 사용자 요구 "세트 데이터 저장" 미충족. 대체 호출.
function CorrectionModal({
  match, preview, onPreview, onApply, reason, setReason, busy, onClose, bracketId, bracket, allMatches, onReload,
}: {
  match: AdminMatch;
  preview: any | null;
  onPreview: (m: AdminMatch, newWinner: BrSide) => void | Promise<void>;
  onApply: () => void | Promise<void>;
  reason: string;
  setReason: (v: string) => void;
  busy: boolean;
  onClose: () => void;
  bracketId: string;
  // V70 QA 리오픈 RR3 (2026-10-23) — 영향 매치 상세에서 라운드 라벨/참가자명 표시용.
  bracket: AdminBracket | null;
  allMatches: AdminMatch[];
  onReload: () => void | Promise<void>;
}) {
  // 세트 리스트 (기존 저장된 세트 결과)
  const sets = match.sets || [];
  const need = Math.ceil(match.bestOf / 2);
  const setsA = sets.filter((s: any) => s.winnerSide === 'A').length;
  const setsB = sets.filter((s: any) => s.winnerSide === 'B').length;
  const currentWinner = match.winnerSide;

  // V70 QA 리오픈 NNN1 (2026-10-23) — BO N 전체 세트 슬롯 표시 + 미경기('none') 옵션.
  //   이전엔 recorded sets 수만 표시 (예 BO5 중 4세트만 쳤으면 4개 라디오). 사용자 요구:
  //   BO1=1, BO3=3, BO5=5, BO7=7, BO9=9 라디오 전부 표시, 미경기 선택 가능.
  //   유효성: 승리 조건 (ceil(bestOf/2)) 충족한 세트까지만 유효, 이후는 미경기(none) 강제.
  //   저장 시 FE 가 'none' 세트를 payload 에서 제외 → BE 스키마 (A/B) 호환 유지.
  type SetWinner = 'A' | 'B' | 'none';
  const initSets: Array<{ setNo: number; winnerSide: SetWinner }> = [];
  for (let i = 1; i <= match.bestOf; i++) {
    const recorded = sets.find((s: any) => s.setNo === i);
    initSets.push({ setNo: i, winnerSide: recorded ? (recorded.winnerSide as 'A' | 'B') : 'none' });
  }
  const [editedSets, setEditedSets] = useState<Array<{ setNo: number; winnerSide: SetWinner }>>(initSets);
  const [localBusy, setLocalBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  // V70 QA 리오픈 N2 (2026-10-22) — 영향 매치 미리보기 별도 중첩 모달 상태.
  const [previewOpen, setPreviewOpen] = useState(false);

  // 승자 계산 — 미경기(none) 제외.
  //   순차적으로 세트를 순회, A/B 승 누적. 승리 조건 도달 즉시 승자 확정. 이후 세트는 "결과 미인정" (승자 확정 후 미경기 처리).
  //   사용자 지시 "승부가 확정된 이후의 세트에 승패가 입력되지 않도록 검증".
  let winnerLocked: BrSide | null = null;
  let countA = 0, countB = 0;
  let hasPostWinnerInput = false;
  for (const s of editedSets) {
    if (winnerLocked !== null && (s.winnerSide === 'A' || s.winnerSide === 'B')) {
      hasPostWinnerInput = true;
    }
    if (winnerLocked !== null) continue; // 승자 확정 후 세트는 집계 X
    if (s.winnerSide === 'A') countA++;
    else if (s.winnerSide === 'B') countB++;
    if (countA >= need) winnerLocked = 'A';
    else if (countB >= need) winnerLocked = 'B';
  }
  const newSetsA = countA;
  const newSetsB = countB;
  const newWinner: BrSide | null = winnerLocked;

  // 변경 감지 — 기존 sets 와 비교 (recorded 만 비교; 미경기(none)는 새 상태)
  const serializedOriginal = JSON.stringify(sets.map((s: any) => ({ setNo: s.setNo, winnerSide: s.winnerSide })));
  const serializedEdited = JSON.stringify(editedSets.filter((s) => s.winnerSide !== 'none').map((s) => ({ setNo: s.setNo, winnerSide: s.winnerSide })));
  const changed = serializedOriginal !== serializedEdited;
  const winnerChanged = newWinner !== null && newWinner !== currentWinner;

  // V70 QA 리오픈 #9 (2026-10-22) — correctMatchSets 신규 Callable 로 세트 데이터 실제 저장.
  //   사용자 지시: "변경한 세트 데이터가 서버에 실제 저장" — 기존 applyCorrection (승자만 변경) 대체.
  // V70 QA 리오픈 RR1 (2026-10-23) — 세트만 수정되고 승자 미변경 상태도 저장 허용.
  //   이전엔 winnerChanged 체크로 저장 거부 → 사용자 지시 "스코어도 수정되면 활성화" 미충족.
  //   세트 승자가 변경되면 저장 (BE correctMatchSets 는 승자 재계산 로직 자체 보유).
  const handleApplySetsEdit = async () => {
    if (!changed) return;
    if (!reason.trim()) { setLocalErr('정정 사유 필수.'); return; }
    if (newWinner === null) {
      setLocalErr(`승리 조건(${need}세트) 미달 — 세트 결과를 다시 확인하세요.`);
      return;
    }
    if (hasPostWinnerInput) {
      setLocalErr('승자 확정 이후 세트에 결과가 입력되어 있습니다. 해당 세트는 "미경기" 로 변경하세요.');
      return;
    }
    setLocalBusy(true); setLocalErr(null);
    try {
      const fns = getFunctions();
      // NNN1 (2026-10-23): 'none' (미경기) 세트는 payload 에서 제외 — BE 스키마 (A/B) 호환.
      //   유효 세트 (실제 진행된 세트) 만 저장. 승자는 BE 가 재계산.
      const payload = editedSets
        .filter((s): s is { setNo: number; winnerSide: 'A' | 'B' } => s.winnerSide === 'A' || s.winnerSide === 'B')
        .map((s) => ({ setNo: s.setNo, winnerSide: s.winnerSide }));
      await httpsCallable(fns, 'correctMatchSets')({
        matchId: match.matchId,
        editedSets: payload,
        reason,
        impactAcknowledged: true,
        clientRequestId: `cms-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      await onReload();
      onClose();
    } catch (e: any) {
      setLocalErr(e?.message || String(e));
    } finally {
      setLocalBusy(false);
    }
  };
  void bracketId; // 참조 유지 (사용 안 함)

  return (
    <div style={brOverlayStyle} onClick={() => !busy && onClose()}>
      <div style={{ ...brModalStyle, maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>스코어 수정 / 결과 정정</h3>
        {(() => {
          // V70 QA 리오픈 RR3 재수정 (2026-10-23) — 라운드/참가자명 표시 보강.
          const cfg = bracket?.roundConfig.find((c) => c.roundNo === match.roundNo);
          const roundLabel = cfg && bracket ? roundLabelOf(cfg, bracket.totalRounds) : `R${match.roundNo}`;
          const nameOf = (pid: string | null): string => {
            if (!pid || !bracket) return '(미정)';
            const p = bracket.participants.find((pp: any) => pp.publicParticipantId === pid);
            if (!p) return '(참가자 미매칭)';
            if ((p as any).mode === 'team' && (p as any).teamName) return (p as any).teamName as string;
            const anyP = p as any;
            const n = (p as any).displayName || anyP.ownerDisplayName;
            return n && String(n).trim() && n !== '-' ? String(n).trim() : '-';
          };
          const aName = nameOf(match.playerA.publicParticipantId);
          const bName = nameOf(match.playerB.publicParticipantId);
          const winnerName = currentWinner === 'A' ? aName : currentWinner === 'B' ? bName : '-';
          return (
            <>
              <div style={{ fontSize: 11, color: '#64748b', margin: '4px 0 10px' }}>
                {roundLabel} · {match.bestOf}판 {need}승제
              </div>
              {/* 현재 결과 — 참가자명 포함 */}
              <div style={{ padding: 10, background: '#f1f5f9', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>
                <div style={{ fontWeight: 600 }}>현재 결과</div>
                <div style={{ marginTop: 4 }}>
                  <strong>{aName}</strong> {setsA} : {setsB} <strong>{bName}</strong> · 승자 <strong>{winnerName}</strong>
                </div>
              </div>
            </>
          );
        })()}

        {/* 세트별 재입력 — V70 QA 리오픈 RR3 (2026-10-23): A/B 라디오를 참가자명 라디오로.
              V70 QA 리오픈 NNN1 (2026-10-23): BO N 전체 세트 슬롯 노출 + '미경기' 옵션 + 승자 확정 이후 세트 자동 미경기. */}
        {(() => {
          const nameOf = (pid: string | null): string => {
            if (!pid || !bracket) return '(미정)';
            const p = bracket.participants.find((pp: any) => pp.publicParticipantId === pid);
            if (!p) return '(참가자 미매칭)';
            if ((p as any).mode === 'team' && (p as any).teamName) return (p as any).teamName as string;
            const anyP = p as any;
            const n = (p as any).displayName || anyP.ownerDisplayName;
            return n && String(n).trim() && n !== '-' ? String(n).trim() : '-';
          };
          const aName = nameOf(match.playerA.publicParticipantId);
          const bName = nameOf(match.playerB.publicParticipantId);
          const newWinnerName = newWinner === 'A' ? aName : newWinner === 'B' ? bName : '(미결정)';
          // 승자 확정 세트 인덱스 — 그 이후 세트는 "미경기" 자동 처리 (사용자 지시).
          let lockedAtIdx = -1;
          let runA = 0, runB = 0;
          for (let i = 0; i < editedSets.length; i++) {
            const w = editedSets[i].winnerSide;
            if (w === 'A') runA++;
            else if (w === 'B') runB++;
            if (runA >= need || runB >= need) { lockedAtIdx = i; break; }
          }
          const setWinner = (idx: number, w: SetWinner) => {
            setEditedSets((prev) => {
              const nx = prev.map((x, i) => i === idx ? { ...x, winnerSide: w } : x);
              // 승자 확정 이후 세트 자동 미경기 처리
              let a = 0, b = 0, locked = -1;
              for (let i = 0; i < nx.length; i++) {
                const ww = nx[i].winnerSide;
                if (ww === 'A') a++;
                else if (ww === 'B') b++;
                if (a >= need || b >= need) { locked = i; break; }
              }
              if (locked !== -1) {
                for (let i = locked + 1; i < nx.length; i++) nx[i] = { ...nx[i], winnerSide: 'none' };
              }
              return nx;
            });
          };
          return (
            <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>세트별 승자 수정 ({match.bestOf}판 {need}승제)</div>
              {editedSets.map((s, idx) => {
                const postLocked = lockedAtIdx !== -1 && idx > lockedAtIdx;
                return (
                  <div key={s.setNo} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 4, flexWrap: 'wrap', opacity: postLocked ? 0.6 : 1 }}>
                    <span style={{ minWidth: 50 }}>세트 {s.setNo}</span>
                    <label>
                      <input type="radio" name={`set-${s.setNo}`}
                        checked={s.winnerSide === 'A'} disabled={postLocked}
                        onChange={() => setWinner(idx, 'A')} /> {aName}
                    </label>
                    <label>
                      <input type="radio" name={`set-${s.setNo}`}
                        checked={s.winnerSide === 'B'} disabled={postLocked}
                        onChange={() => setWinner(idx, 'B')} /> {bName}
                    </label>
                    <label>
                      <input type="radio" name={`set-${s.setNo}`}
                        checked={s.winnerSide === 'none'}
                        onChange={() => setWinner(idx, 'none')} /> 미경기
                    </label>
                    {postLocked && <span style={{ fontSize: 10, color: '#64748b' }}>(승자 확정 후 — 자동 미경기)</span>}
                  </div>
                );
              })}
              <div style={{ marginTop: 8, padding: 6, background: newWinner !== currentWinner ? '#fef3c7' : '#f1f5f9', borderRadius: 4, fontSize: 11 }}>
                수정 후: <strong>{aName}</strong> {newSetsA} : {newSetsB} <strong>{bName}</strong> · 승자 <strong>{newWinnerName}</strong>
                {newWinner === null && <span style={{ color: '#991b1b' }}> ⚠ 승리 조건({need}세트) 미달 — 세트를 더 수정하세요.</span>}
              </div>
            </div>
          );
        })()}

        {/* 미리보기 트리거 — V70 QA 리오픈 RR1 재수정 (2026-10-23):
              승자가 바뀔 때만 노출 (영향 매치 reset 가능). 세트만 수정은 영향 매치 없음. */}
        {winnerChanged && !preview && (
          <button
            style={brSecondaryBtn}
            onClick={() => onPreview(match, newWinner as BrSide)}
            disabled={busy || newWinner === null}
          >영향 매치 미리보기</button>
        )}

        {/* V70 QA 리오픈 N2 (2026-10-22) — 영향 매치 미리보기는 별도 중첩 모달로 분리.
              이전엔 CorrectionModal 안에 inline 노출 → 세로 공간 부족 시 보이지 않거나 레이어 혼란.
              현재: preview 데이터가 들어왔을 때 요약만 표시, 상세는 "📋 영향 매치 상세보기" 버튼으로 중첩 모달 */}
        {preview && (
          <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, margin: '10px 0', fontSize: 12 }}>
            ⚠ 승자 변경 시 영향 매치 <strong>{preview.willResetMatches.length}건</strong>의 기록이 초기화됩니다. (history 에 보존)
            <button
              style={{
                marginLeft: 8, padding: '2px 8px', background: '#fff', border: '1px solid #fcd34d',
                color: '#92400e', borderRadius: 4, cursor: 'pointer', fontSize: 11,
              }}
              onClick={() => setPreviewOpen(true)}
            >📋 영향 매치 상세보기</button>
          </div>
        )}
        {previewOpen && preview && (
          // z-index 1500 (brOverlayStyle 1000 보다 상위) + Portal 미사용 — stacking context 보장.
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
              zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
            onClick={() => setPreviewOpen(false)}
          >
            <div
              style={{
                background: '#fff', borderRadius: 10, padding: 16, width: '100%',
                maxWidth: 560, maxHeight: '85vh', overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📋 영향 매치 상세 ({preview.willResetMatches.length}건)</h3>
                <button
                  style={{ padding: '4px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                  onClick={() => setPreviewOpen(false)}
                >닫기</button>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                승자 변경 시 아래 매치들의 기록이 초기화됩니다. 기존 세트/승자 데이터는 bracketHistory 에 감사 이력으로 보존됩니다.
              </div>
              {/* V70 QA 리오픈 RR3 재수정 (2026-10-23) — 영향 매치 상세:
                    'R2' → '4강' 등 라운드 라벨, '승자 A/B' → 실제 참가자명 표시.
                    allMatches 로 각 영향 매치의 playerA/B publicParticipantId 조회 → bracket.participants 에서 이름 매핑. */}
              <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 4 }}>
                {preview.willResetMatches.map((im: any) => {
                  const cfg = bracket?.roundConfig.find((c) => c.roundNo === im.roundNo);
                  const roundLabel = cfg && bracket ? roundLabelOf(cfg, bracket.totalRounds) : `R${im.roundNo}`;
                  const fullMatch = allMatches.find((m) => m.matchId === im.matchId);
                  const nameOf = (publicParticipantId: string | null): string => {
                    if (!publicParticipantId || !bracket) return '(미정)';
                    const p = bracket.participants.find((pp: any) => pp.publicParticipantId === publicParticipantId);
                    if (!p) return '(참가자 미매칭)';
                    if ((p as any).mode === 'team' && (p as any).teamName) return (p as any).teamName as string;
                    const anyP = p as any;
                    const n = (p as any).displayName || anyP.ownerDisplayName;
                    return n && String(n).trim() && n !== '-' ? String(n).trim() : '-';
                  };
                  const aName = fullMatch ? nameOf(fullMatch.playerA.publicParticipantId) : '(미정)';
                  const bName = fullMatch ? nameOf(fullMatch.playerB.publicParticipantId) : '(미정)';
                  const winnerName = im.currentWinner === 'A' ? aName : im.currentWinner === 'B' ? bName : '(미결정)';
                  return (
                    <div key={im.matchId} style={{ padding: 8, borderBottom: '1px solid #e5e7eb', fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{roundLabel}</div>
                      <div style={{ color: '#334155', marginTop: 2 }}>
                        <strong>{aName}</strong> vs <strong>{bName}</strong>
                      </div>
                      <div style={{ color: '#64748b', marginTop: 2 }}>
                        현재 상태: {im.currentStatus} · 승자: <strong>{winnerName}</strong> · 세트 {im.sets.length}건
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button
                  style={{ padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                  onClick={() => setPreviewOpen(false)}
                >확인</button>
              </div>
            </div>
          </div>
        )}

        {/* 정정 사유 — V70 QA 리오픈 RR1 재수정 (2026-10-23):
              세트 변경 (changed) 시 항상 노출. 승자 미변경 세트 수정도 사유 필수. */}
        {changed && (
          <div>
            <label style={{ fontSize: 12 }}>정정 사유 (1-500자, 필수):</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              style={{ width: '100%', minHeight: 60, padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, boxSizing: 'border-box', marginTop: 4 }}
            />
          </div>
        )}

        {localErr && <div style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginTop: 10 }}>{localErr}</div>}
        {/* V70 QA 리오픈 R1 (2026-10-22) — [적용하기] 버튼 항상 노출.
              이전엔 4중 조건(changed && winnerChanged && preview && reason) 모두 충족해야 노출 → 사용자가 미리보기 실행 전엔 못 찾음.
              현재: 버튼 항상 노출, 조건 미충족 시 disabled + 사유 안내 (왜 비활성화 되었는지). */}
        {(() => {
          // V70 QA 리오픈 RR1 재수정 (2026-10-23) — canApply 조건 완화.
          //   세트 (스코어) 변경 OR 승자 변경 → 활성화. 둘 다 아니면 비활성화.
          //   미리보기(preview)는 승자가 바뀔 때만 필수 (영향 매치 reset 발생 → 사용자 확인 필요).
          //   승자 미변경 세트만 수정 시: 영향 매치 없음 → preview 불필요.
          const previewRequired = winnerChanged;
          const canApply = changed && !!reason.trim() && (!previewRequired || !!preview) && !busy && !localBusy;
          const whyDisabled =
            !changed ? '먼저 세트 승자를 수정하세요 (스코어 변경).' :
            !reason.trim() ? '정정 사유를 입력하세요 (필수).' :
            (previewRequired && !preview) ? '승자가 바뀝니다 — [영향 매치 미리보기] 를 먼저 실행하세요.' :
            '';
          return (
            <>
              {whyDisabled && !busy && !localBusy && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, padding: 6, background: '#f1f5f9', borderRadius: 4 }}>
                  💡 {whyDisabled}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
                <button style={brSecondaryBtn} onClick={onClose} disabled={busy || localBusy}>취소</button>
                <button
                  style={{
                    ...brDangerBtn,
                    background: canApply ? '#dc2626' : '#cbd5e1',
                    color: canApply ? '#fff' : '#64748b',
                    cursor: canApply ? 'pointer' : 'not-allowed',
                    opacity: canApply ? 1 : 0.7,
                  }}
                  onClick={handleApplySetsEdit}
                  disabled={!canApply}
                >
                  {localBusy ? '저장 중…' : '적용하기'}
                </button>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

// V70 QA 신규 기능 #1·#3 (2026-10-22) — 매치 메타 (일정/중계) 설정 모달.
//   schedule: 단일 날짜 or 기간. 미설정 가능. 삭제 가능.
//   broadcast: enabled 체크 시만 입력 노출. 종료 일시 역순 거부.
//   BE updateMatchMeta Callable 호출. 매치 결과·승자·sets 변경 없음.
function MatchMetaModal({
  match, onClose, onSaved,
}: {
  match: AdminMatch; onClose: () => void; onSaved: () => void | Promise<void>;
}) {
  const initSchedule = (match as any).schedule || null;
  const initBroadcast = (match as any).broadcast || null;
  const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(!!initSchedule);
  const [scheduleKind, setScheduleKind] = useState<'single' | 'range'>(initSchedule?.kind || 'single');
  const [scheduleStart, setScheduleStart] = useState<string>(initSchedule?.startDate || '');
  const [scheduleEnd, setScheduleEnd] = useState<string>(initSchedule?.endDate || '');

  const [broadcastEnabled, setBroadcastEnabled] = useState<boolean>(!!initBroadcast?.enabled);
  const toKstInput = (ts: any): string => {
    if (!ts) return '';
    const ms = typeof ts.toMillis === 'function' ? ts.toMillis() : (ts._seconds ? ts._seconds * 1000 : 0);
    if (!ms) return '';
    const d = new Date(ms + 9 * 3600 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  };
  const [broadcastStart, setBroadcastStart] = useState<string>(toKstInput(initBroadcast?.startAt));
  const [broadcastEnd, setBroadcastEnd] = useState<string>(toKstInput(initBroadcast?.endAt));
  const [broadcastLocation, setBroadcastLocation] = useState<string>(initBroadcast?.location || '');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const kstInputToMs = (s: string): number => {
    if (!s) return NaN;
    // 입력값은 "YYYY-MM-DDTHH:mm" (KST 로 해석). KST = UTC+9.
    const [date, time] = s.split('T');
    if (!date || !time) return NaN;
    const [y, mo, d] = date.split('-').map(Number);
    const [h, mi] = time.split(':').map(Number);
    const utcMs = Date.UTC(y, mo - 1, d, h, mi, 0, 0) - 9 * 3600 * 1000;
    return utcMs;
  };

  const handleSave = async () => {
    setErr(null); setBusy(true);
    try {
      // Validation
      let schedulePayload: any = undefined;
      if (!scheduleEnabled) {
        schedulePayload = null;
      } else {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduleStart)) throw new Error('일정 시작일 YYYY-MM-DD 형식.');
        if (scheduleKind === 'range') {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduleEnd)) throw new Error('기간 종료일 YYYY-MM-DD 형식.');
          if (scheduleEnd < scheduleStart) throw new Error('기간 종료일이 시작일보다 빠릅니다.');
          schedulePayload = { kind: 'range', startDate: scheduleStart, endDate: scheduleEnd };
        } else {
          schedulePayload = { kind: 'single', startDate: scheduleStart, endDate: null };
        }
      }

      let broadcastPayload: any = undefined;
      if (!broadcastEnabled) {
        broadcastPayload = null;
      } else {
        const sMs = kstInputToMs(broadcastStart);
        const eMs = kstInputToMs(broadcastEnd);
        if (!Number.isFinite(sMs) || !Number.isFinite(eMs)) throw new Error('중계 일시 입력 필요.');
        if (eMs <= sMs) throw new Error('중계 종료 일시가 시작 일시보다 빠르거나 같습니다.');
        if (!broadcastLocation.trim()) throw new Error('중계 장소 입력 필요.');
        broadcastPayload = { enabled: true, startAt: sMs, endAt: eMs, location: broadcastLocation.trim() };
      }

      const fns = getFunctions();
      await httpsCallable(fns, 'updateMatchMeta')({
        matchId: match.matchId,
        schedule: schedulePayload,
        broadcast: broadcastPayload,
        clientRequestId: `meta-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      await onSaved();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally { setBusy(false); }
  };

  return (
    <div style={brOverlayStyle} onClick={onClose}>
      <div style={{ ...brModalStyle, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>경기 일정 / 중계 설정</h3>
        <div style={{ fontSize: 11, color: '#64748b', margin: '4px 0 10px' }}>
          R{match.roundNo} · slot {match.slotIndex} · {match.bestOf}판 {Math.ceil(match.bestOf / 2)}승제
        </div>
        {err && <div style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>{err}</div>}

        {/* 일정 */}
        <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
            <input type="checkbox" checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} />
            경기 일정 설정
          </label>
          {scheduleEnabled && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                <label><input type="radio" checked={scheduleKind === 'single'} onChange={() => setScheduleKind('single')} /> 단일 날짜</label>
                <label><input type="radio" checked={scheduleKind === 'range'} onChange={() => setScheduleKind('range')} /> 기간</label>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <span>시작일:</span>
                <input type="date" value={scheduleStart} onChange={(e) => setScheduleStart(e.target.value)} style={{ padding: 4, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                {scheduleKind === 'range' && (
                  <>
                    <span>종료일:</span>
                    <input type="date" value={scheduleEnd} onChange={(e) => setScheduleEnd(e.target.value)} style={{ padding: 4, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 중계 */}
        <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
            <input type="checkbox" checked={broadcastEnabled} onChange={(e) => setBroadcastEnabled(e.target.checked)} />
            중계 진행
          </label>
          {broadcastEnabled && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
              <div>
                중계 시작 (KST):{' '}
                <input type="datetime-local" value={broadcastStart} onChange={(e) => setBroadcastStart(e.target.value)} style={{ padding: 4, border: '1px solid #cbd5e1', borderRadius: 4 }} />
              </div>
              <div>
                중계 종료 (KST):{' '}
                <input type="datetime-local" value={broadcastEnd} onChange={(e) => setBroadcastEnd(e.target.value)} style={{ padding: 4, border: '1px solid #cbd5e1', borderRadius: 4 }} />
              </div>
              <div>
                장소:{' '}
                <input type="text" value={broadcastLocation} onChange={(e) => setBroadcastLocation(e.target.value)}
                  maxLength={200} placeholder="자유 텍스트 (최대 200자)"
                  style={{ padding: 4, border: '1px solid #cbd5e1', borderRadius: 4, width: 300 }} />
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={brSecondaryBtn} onClick={onClose} disabled={busy}>취소</button>
          <button style={brPrimaryBtn} onClick={handleSave} disabled={busy}>저장</button>
        </div>
      </div>
    </div>
  );
}

export function SetInputModal({
  match, bracket, onClose, onRecord, busy,
}: {
  match: AdminMatch; bracket: AdminBracket | null; onClose: () => void; onRecord: (setNo: number, winner: BrSide) => void; busy: boolean;
}) {
  const setsA = match.sets.filter((s) => s.winnerSide === 'A').length;
  const setsB = match.sets.filter((s) => s.winnerSide === 'B').length;
  const nextSetNo = match.sets.length + 1;
  const need = Math.ceil(match.bestOf / 2);
  // V70G8J — A/B 대신 실제 팀명(개인전은 참가자명). 저장 값은 기존 슬롯('A'|'B') 그대로.
  const participants = (bracket?.participants || []) as any[];
  const isTeam = participants.some((p) => p?.mode === 'team');
  const aName = matchSideLabel(participants, match.playerA.publicParticipantId, 'A', isTeam);
  const bName = matchSideLabel(participants, match.playerB.publicParticipantId, 'B', isTeam);
  const sideBtn = (side: BrSide, name: string, color: string) => (
    <button
      style={{ ...brPrimaryBtn, background: color, flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
      onClick={() => onRecord(nextSetNo, side)}
      disabled={busy}
      title={`${side} · ${name}`}
      data-testid={`set-winner-${side}`}
    >
      <span style={{ fontSize: 11, opacity: 0.85 }}>{isTeam ? '팀' : '참가자'} {side}</span>
      <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name} 승</span>
    </button>
  );
  return (
    <div style={brOverlayStyle} onClick={onClose}>
      <div style={brModalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>세트 {nextSetNo} 승자 입력</h3>
        <div style={{ fontSize: 12, color: '#64748b', margin: '6px 0 10px', overflowWrap: 'anywhere' }} data-testid="set-input-score">
          {/* V70 QA 추가 수정 #4 (2026-10-22) — "BO3" → "3판 2승제" */}
          {match.bestOf}판 {need}승제 · 현재 스코어 {aName} {setsA} : {setsB} {bName}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {sideBtn('A', aName, '#059669')}
          {sideBtn('B', bName, '#2563eb')}
        </div>
        <div style={{ textAlign: 'right', marginTop: 10 }}>
          <button style={brSecondaryBtn} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

// V70 QA 신규 기능 #2 (2026-10-22) — 게임대회 공지사항 Admin CRUD 섹션.
//   리치 에디터 (RichTextEditor) + 이미지 업로드 (useAnnouncementUpload) 재사용.
//   서버에서 createBoardPost/updateBoardPost/deleteBoardPost Callable 가 Admin 권한 재검증.
// V70 QA 리오픈 #5 (2026-10-22) — 결과만 공지 섹션.
//   승인 참가자 0명/1명 상태에서 노출. 대진표 생성 없이 결과 게시.
//   관리자 입력: 제목 + RichTextEditor 본문 + (선택) 우승자 지정.
//   참가자가 1명이면 그 사람을 우승자로 자동 제안.
function ResultAnnouncementSection({ sourceFormId, preview }: { sourceFormId: string; preview: BracketPreview }) {
  const [existing, setExisting] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ title: string; contentHtml: string; winnerPublicParticipantId: string | null; winnerName: string | null; winnerKind: 'solo' | 'team' | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { uploadFile } = useAnnouncementUpload();

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const fns = getFunctions();
      const r = await httpsCallable<{ sourceFormId: string }, { announcement: any | null }>(fns, 'getResultAnnouncement')({ sourceFormId });
      setExisting(r.data.announcement);
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setLoading(false); }
  }, [sourceFormId]);

  useEffect(() => { reload(); }, [reload]);

  const openEditor = () => {
    if (existing) {
      setEditing({
        title: existing.title, contentHtml: existing.contentHtml,
        winnerPublicParticipantId: existing.winnerPublicParticipantId,
        winnerName: existing.winnerName,
        winnerKind: existing.winnerKind,
      });
    } else {
      // 신규 작성 — 참가자 1명이면 자동 우승자 제안
      let winner: { winnerPublicParticipantId: string | null; winnerName: string | null; winnerKind: 'solo' | 'team' | null } = {
        winnerPublicParticipantId: null, winnerName: null, winnerKind: null,
      };
      if (preview.approvedCount === 1 && preview.participants.length === 1) {
        const p = preview.participants[0];
        winner = {
          winnerPublicParticipantId: p.applicationId, // 임시로 applicationId 사용 — BE 는 publicParticipantId 받음
          winnerName: p.mode === 'team' ? (p.teamName || p.displayName || '-') : (p.displayName || '-'),
          winnerKind: p.mode,
        };
      }
      setEditing({ title: '', contentHtml: '', ...winner });
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    const att = await uploadFile(file, { trackProgress: false });
    return att.url;
  };

  const handleSave = async () => {
    if (!editing) return;
    const title = editing.title.trim();
    if (!title) { setErr('제목 공란 금지'); return; }
    const contentHtml = sanitizeAnnouncementHtml(editing.contentHtml || '').trim();
    if (!contentHtml) { setErr('본문 공란 금지'); return; }
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'upsertResultAnnouncement')({
        sourceFormId, title, contentHtml,
        winnerPublicParticipantId: editing.winnerPublicParticipantId,
        winnerName: editing.winnerName,
        winnerKind: editing.winnerKind,
      });
      setEditing(null);
      await reload();
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('결과 공지를 삭제하시겠습니까? (감사 이력은 보존)')) return;
    setBusy(true); setErr(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'deleteResultAnnouncement')({ sourceFormId });
      await reload();
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <section style={{ marginTop: 16, padding: 12, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6 }}>
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e40af' }}>
        🏁 결과만 공지 (대진표 없이)
      </h4>
      <div style={{ fontSize: 11, color: '#1e40af', marginTop: 4 }}>
        승인 참가자 {preview.approvedCount}명 — 대진이 성립하지 않을 때 결과만 게시하는 모드입니다.
      </div>
      {err && <div style={{ marginTop: 8, padding: 6, background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 11 }}>{err}</div>}
      {loading && <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>로딩 중…</div>}
      {!loading && !existing && (
        <button
          style={{ marginTop: 8, padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
          onClick={openEditor}
        >+ 결과 공지 작성</button>
      )}
      {!loading && existing && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{existing.title}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {tournamentAuthorLabel(existing.createdByName)} · {new Date((existing.updatedAt?.toMillis ? existing.updatedAt.toMillis() : (existing.updatedAt?._seconds ?? 0) * 1000)).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
            {existing.winnerName ? ` · 🏆 ${existing.winnerKind === 'team' ? '팀' : '우승'}: ${existing.winnerName}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button style={brSecondaryBtn} onClick={openEditor} disabled={busy}>수정</button>
            <button style={brDangerBtn} onClick={handleDelete} disabled={busy}>삭제</button>
          </div>
        </div>
      )}

      {editing && (
        <div style={brOverlayStyle} onClick={() => !busy && setEditing(null)}>
          <div style={{ ...brModalStyle, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>🏁 결과 공지 {existing ? '수정' : '작성'}</h3>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>제목</label>
              <input
                type="text" value={editing.title} maxLength={200}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                style={{ width: '100%', padding: 6, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, marginTop: 4, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>본문</label>
              <RichTextEditor
                value={editing.contentHtml}
                onChange={(v) => setEditing({ ...editing, contentHtml: v })}
                onImageUpload={handleImageUpload}
                placeholder="결과 공지 내용을 입력하세요 (예: 대회 중단 안내, 수상자 지정 사유 등)"
              />
            </div>
            {preview.approvedCount === 1 && (
              <div style={{ marginTop: 10, padding: 8, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4, fontSize: 12, color: '#92400e' }}>
                💡 승인 참가자 1명 — 우승자로 자동 지정됨: <strong>{editing.winnerName || '-'}</strong>
                {editing.winnerKind === 'team' ? ' (팀명)' : ' (사원 이름)'}
              </div>
            )}
            {preview.approvedCount === 0 && (
              <div style={{ marginTop: 10, padding: 8, background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, color: '#334155' }}>
                승인 참가자 0명 — 우승자 없이 결과/안내만 게시됩니다.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button style={brSecondaryBtn} onClick={() => setEditing(null)} disabled={busy}>취소</button>
              <button style={brPrimaryBtn} onClick={handleSave} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// V70G5A Bug 6 — BoardAdminSection 함수 제거.
//   공지사항은 대시보드 전역 AnnouncementsSection (TournamentDashboardPage.tsx) 로 완전 이관됨.
//   기존 공지사항 데이터 (listBoardPosts/getBoardPost/createBoardPost/updateBoardPost/deleteBoardPost
//   BE Callable) 는 보존 — Admin 양식관리 쪽 UI 만 제거.

const brPrimaryBtn: React.CSSProperties = { padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const brSecondaryBtn: React.CSSProperties = { padding: '8px 14px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 13 };
const brDangerBtn: React.CSSProperties = { padding: '8px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const brPill: React.CSSProperties = { display: 'inline-block', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 };
const brOverlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const brModalStyle: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: 16, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' };
